/**
 * AppShell.tsx — application shell, router, and department dispatcher.
 *
 * Owns: the sidebar navigation, the URL ↔ department mapping, lazy
 *       registration of every department-level view (`*View.tsx`), the
 *       Clockify / time-tracking saved-views state used by the time
 *       dashboards, the global metric-settings + metric-form orchestration,
 *       the "What's New" dialog trigger, and a large amount of legacy
 *       per-department state that has not yet been extracted.
 * Used by: `App.tsx` renders this as the sole top-level child once the
 *       user is authenticated.
 * Depends on: `routeConfig.ts`, every `*View.tsx` department component
 *       (lazy), `lazyWithRetry`, `services/api.ts`, MUI primitives.
 * Notes: Frozen file (operating-rules.md §4). Renamed from
 *       `QuarterlyScorecard.tsx` 2026-05-07 per ticket 0b-03 (full git
 *       history preserved via `git mv`). At 4,520 LOC this is the single
 *       highest-value extraction target on the frontend — Phase 1 will
 *       split sidebar, routing, and per-department state into separate
 *       files. The `interface AppShellProps` props (`quarter`, `year`,
 *       `refreshSignal`, metric-form callbacks) are vestigial from the
 *       pre-rename scorecard role and survive only because App.tsx still
 *       passes them.
 */
import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, Suspense, useRef } from 'react';
import ConfirmDialog from './shared/ConfirmDialog';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import { useLocation, useNavigate } from 'react-router-dom';
import { lazyWithRetry, preloadLazyPages } from '../utils/lazyWithRetry';
import NewVersionWatcher from './shared/NewVersionWatcher';
import { parsePathname, departmentToPath } from '../utils/routeConfig';
import { navItemForPath, navItemByKey } from '../config/navTree';
import HubRail from './hubRail/HubRail';
import type { NavItem, NavNode, NavGroup } from '../config/navTree';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { extractApiError } from '../types/apiError';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
  MenuItem,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  SelectChangeEvent,
  Checkbox,
  Snackbar,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  LinearProgress,
  TableSortLabel,
  Popover,
  ListSubheader,
  ToggleButtonGroup,
  ToggleButton,
  Badge,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  alpha,
  useTheme,
} from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import FactoryIcon from '@mui/icons-material/Factory';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ScheduleIcon from '@mui/icons-material/Schedule';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import PercentIcon from '@mui/icons-material/Percent';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SaveIcon from '@mui/icons-material/Save';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import { ScorecardMetric, QuarterInfo } from '../types';
import { scorecardApi, targetsApi, metricsApi, dashboardsApi, departmentsApi, metricVisibilityApi, aptlyApi, financesApi, settingsApi, ScorecardViewFilters, timeReportsApi, timeTrackingApi, forecastApi, ReportDataSource, inboxApi, createInboxApi, usersApi, ProjectSummary, changelogApi } from '../services/api';
import TimesheetView from './TimesheetView';
import AddValueDialog from './AddValueDialog';
import DateRangeFilter from './DateRangeFilter';
import { Dashboard, Metric } from '../types';
import CreateCustomDashboardDialog from './CreateCustomDashboardDialog';
import EditCustomDashboardDialog from './EditCustomDashboardDialog';
import SettingsIcon from '@mui/icons-material/Settings';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BarChartIcon from '@mui/icons-material/BarChart';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import MetricVisibilityDialog from './MetricVisibilityDialog';
import MetricDrillDownDialog from './MetricDrillDownDialog';
import MyTicketsView from './MyTicketsView';
import { exportToCsv } from '../utils/scorecardHelpers';
import { buildScorecardHtml } from '../utils/scorecardPdf';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PersonIcon from '@mui/icons-material/Person';
import TimerIcon from '@mui/icons-material/Timer';
import InsightsIcon from '@mui/icons-material/Insights';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import ForumIcon from '@mui/icons-material/Forum';
import BusinessIcon from '@mui/icons-material/Business';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import GroupsIcon from '@mui/icons-material/Groups';
import SparklineDetailDialog from './widgets/SparklineDetailDialog';
import WidgetBoard from './widgets/WidgetBoard';
import AIInsightChip from './ai/AIInsightChip';
import LoadingOverlay from './shared/LoadingOverlay';
import FeedbackIcon from '@mui/icons-material/Feedback';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import BugReportIcon from '@mui/icons-material/BugReport';
import { debugService } from '../services/debugService';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import MenuIcon from '@mui/icons-material/Menu';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ChecklistIcon from '@mui/icons-material/Checklist';
import FlagIcon from '@mui/icons-material/Flag';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PsychologyIcon from '@mui/icons-material/Psychology';
import PushPinIcon from '@mui/icons-material/PushPin';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import FeatureRequestDialog from './shared/FeatureRequestDialog';
import WhatsNewDialog from './WhatsNewDialog';
import GlobalQuickAdd from './shared/GlobalQuickAdd';
import TodayIcon from '@mui/icons-material/Today';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import BuildIcon from '@mui/icons-material/Build';
import ContactsIcon from '@mui/icons-material/Contacts';
import DescriptionIcon from '@mui/icons-material/Description';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DynamicFormIcon from '@mui/icons-material/DynamicForm';
import { formsApi } from './forms/formsApi'; // Entry 197: Forms rail unread badge
import FenceIcon from '@mui/icons-material/Fence';
// I5.2 (state.md Entry 9): opt-in flush-layout predicate. The list of
// flush departments lives in `./inbox/flushModules.ts` so the
// "I have my own header chrome" knowledge belongs to the child modules,
// not as a hardcoded department whitelist inside AppShell. AppShell's
// scoped unlock covers only the wrapper Box + title-row block below.
import { isFlushDepartment } from './inbox/flushModules';

// New self-contained finance module (sub-navigation with dashboard, P&L, AR/AP, etc.)
// Perf 3g (Entry 352): Settings is 36 files / ~98KB gzip — 11.3% of the served
// bundle — pinned eager by this single import while 20+ sibling department
// arms were already lazy. Same lazyWithRetry + Suspense pattern as those.
const SettingsPage = lazyWithRetry(() => import('./SettingsPage'));
const FinancesModule = lazyWithRetry(() => import('./finances/FinancesModule'));

const CapacityView = lazyWithRetry(() => import('./capacity/CapacityView'));
const TimeTrackerMinimal = lazyWithRetry(() => import('./timeTracker/TimeTrackerMinimal'));
const TimeReportsView = lazyWithRetry(() => import('./timeReports/TimeReportsView'));
const CallIntelligenceDashboard = lazyWithRetry(() => import('./CallIntelligenceDashboard'));
const OperationsView = lazyWithRetry(() => import('./OperationsView'));
const SalesView = lazyWithRetry(() => import('./SalesView'));
const EOSView = lazyWithRetry(() => import('./EOSView'));
const InboxModule = lazyWithRetry(() => import('./inbox/InboxModule'));
const CommsView = lazyWithRetry(() => import('./comms/CommsView'));
const PersonalDashboard = lazyWithRetry(() => import('./PersonalDashboard'));
const EngineeringView = lazyWithRetry(() => import('./EngineeringView'));
const SmokeTestsPage = lazyWithRetry(() => import('./smoke/SmokeTestsPage'));
const AiMissionControlView = lazyWithRetry(() => import('./missionControl/AiMissionControlView'));
const ContactsDatabaseView = lazyWithRetry(() => import('./ContactsDatabaseView'));
const ClientsDatabaseView = lazyWithRetry(() => import('./ClientsDatabaseView'));
const ReportsView = lazyWithRetry(() => import('./ReportsView'));
const TemplateLibraryView = lazyWithRetry(() => import('./templates/TemplateLibraryView'));
const SchedulingView = lazyWithRetry(() => import('./scheduling/SchedulingView'));
const FormsView = lazyWithRetry(() => import('./forms/FormsView'));
// Proposals (state.md Entry 137) — the Proposal Generator promoted to a
// top-level tool per Michael's gate approval. Owns /proposals/* sub-routes
// (library, wizard, owner catalog, detail); old /sales/proposals/* redirect.
const ProposalsView = lazyWithRetry(() => import('./proposals/ProposalsView'));
// rail v2: To-Dos promoted to a top-level tool (/todos). Reuses the SAME
// graduated todos surface OperationsView renders for /operations/todos
// (canonical-surface reuse) — admins/team get the engine-backed view, others
// the legacy one.
const OperationsTodosView = lazyWithRetry(() => import('./operations/OperationsTodosView'));
// Documentation Nation (state.md Entry 108) — client process documentation
// on the flowchart substrate; same components as the sandbox tile, mounted
// at /documentation via the named export.
const DocumentationNationView = lazyWithRetry(() =>
  import('./engine/documentationNation/DocumentationNationRouter').then(m => ({ default: m.DocumentationNationView })));
const MarketingView = lazyWithRetry(() => import('./MarketingView'));
const KnowledgeBaseView = lazyWithRetry(() => import('./KnowledgeBaseView'));
// Scorecard rebuild P3 (state.md Entry 92): the graduated scorecard —
// live table + registry view + creation wizard + trend modal. Replaces
// the inline god-view for /reports/scorecards and /my-scorecard; the
// inline JSX below stays (unreachable) per the deferred extraction.
const ScorecardsView = lazyWithRetry(() => import('./scorecards/ScorecardsView'));
// Analytics hub (Brain Dead To Read S1-c1, state.md Entry 377).
const AnalyticsHubView = lazyWithRetry(() => import('./analytics/AnalyticsHubView'));


const Sparkline = React.memo(function Sparkline({ data, color = '#C9A961', width = 48, height = 16 }: { data: number[]; color?: string; width?: number; height?: number }) {
  const filtered = data.filter(v => v != null && v !== 0);
  if (filtered.length < 2) return null;
  const min = Math.min(...filtered);
  const max = Math.max(...filtered);
  const range = max - min || 1;
  const points = filtered.map((v, i) => {
    const x = (i / (filtered.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 2) - 1;
    return `${x},${y}`;
  }).join(' ');
  const trend = filtered[filtered.length - 1] >= filtered[0];
  const lineColor = color === 'auto' ? (trend ? '#4caf50' : '#CF6679') : color;
  return (
    <svg width={width} height={height} style={{ display: 'block', opacity: 0.7 }}>
      <polyline points={points} fill="none" stroke={lineColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
});

interface ClockifyFilters {
  userIds?: string[];
  projectIdsExcluded?: string[];
  hideZeroTime?: boolean;
  timeRange?: { preset?: string; startDate?: string; endDate?: string };
  columns?: string[];
}

interface AppShellProps {
  quarter?: number;
  year?: number;
  refreshSignal?: number;
  onOpenMetricSettings?: (metricId: number) => void;
  onEditMetric?: (metricId: number) => void;
  onAddMetric?: () => void;
}

const AppShell: React.FC<AppShellProps> = ({
  quarter,
  year,
  refreshSignal,
  onOpenMetricSettings,
  onEditMetric,
  onAddMetric: _onAddMetric,
}) => {
  const { dialogProps, showConfirm } = useConfirmDialog();
  const sidebarTheme = useTheme();
  const sidebarIsDark = sidebarTheme.palette.mode === 'dark';
  const [scorecard, setScorecard] = useState<ScorecardMetric[]>([]);
  const [quarterInfo, setQuarterInfo] = useState<QuarterInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addValueDialogOpen, setAddValueDialogOpen] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState<Metric | null>(null);
  const [selectedWeek, setSelectedWeek] = useState<string | null>(null);
  const [selectedWeekCurrentValue, setSelectedWeekCurrentValue] = useState<number | undefined>(undefined);
  const [, setAdjustingCell] = useState<string | null>(null);
  const [dateFilterStart, setDateFilterStart] = useState<string | null>(null);
  const [dateFilterEnd, setDateFilterEnd] = useState<string | null>(null);
  const [departmentsOpen, setDepartmentsOpen] = useState(() => {
    const saved = localStorage.getItem('bsg-sidebar-departments');
    return saved === null ? true : saved === 'true';
  });
  const [groupOpen, setGroupOpen] = useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem('bsg-sidebar-groups');
      if (saved) {
        const parsed = JSON.parse(saved);
        if ('Reports & Metrics' in parsed && !('Data' in parsed)) {
          parsed.Data = parsed['Reports & Metrics'];
          delete parsed['Reports & Metrics'];
          localStorage.setItem('bsg-sidebar-groups', JSON.stringify(parsed));
        }
        return parsed;
      }
    } catch { /* ignore */ }
    return { Tools: true, Company: true, Data: true };
  });
  useEffect(() => {
    localStorage.setItem('bsg-sidebar-departments', String(departmentsOpen));
    // CD-12 / 4A: HubRail is 264px expanded / 72px collapsed (was 220/64). The
    // frozen App.tsx content offsets read this var (fallback 220px), so bumping
    // it here flows the wider rail through with no App.tsx change.
    const w = departmentsOpen ? '264px' : '72px';
    document.documentElement.style.setProperty('--bsg-sidebar-width', w);
  }, [departmentsOpen]);
  useEffect(() => {
    localStorage.setItem('bsg-sidebar-groups', JSON.stringify(groupOpen));
  }, [groupOpen]);
  const [featureRequestOpen, setFeatureRequestOpen] = useState(false);
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const [devModeOn, setDevModeOn] = useState(() => debugService.isEnabled);
  const [whatsNewUnread, setWhatsNewUnread] = useState(0);

  useEffect(() => {
    changelogApi.getUnreadCount().then(r => {
      if (r.success) setWhatsNewUnread(r.data.count);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('whats-new') === '1') {
      setWhatsNewOpen(true);
      const url = new URL(window.location.href);
      url.searchParams.delete('whats-new');
      window.history.replaceState({}, '', url.pathname + url.search);
    }
  }, []);

  // My Week – My Day bar + weekly timesheet (state only; effect added after auth is available)
  const [myDaySummary, setMyDaySummary] = useState<ProjectSummary[]>([]);
  const [timesheetExpanded, setTimesheetExpanded] = useState(true);
  const weekDateRange = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const day = today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() - (day === 0 ? 6 : day - 1));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 7);
    return { startDate: monday.toISOString(), endDate: sunday.toISOString() };
  }, []);

  const [inboxUnreadCount, setInboxUnreadCount] = useState(0);
  const [helpDeskUnreadCount, setHelpDeskUnreadCount] = useState(0);
  const [myTicketsCount, setMyTicketsCount] = useState(0);
  const [formsUnreadCount, setFormsUnreadCount] = useState(0); // Entry 197
  useEffect(() => {
    const loadCounts = async () => {
      try {
        // Perf 3f: countOnly skips the list query (and its inbox_messages
        // materialisation) server-side — the badge only reads `total`.
        const res = await inboxApi.getThreads({ status: 'open', limit: 1, countOnly: 1 } as any);
        setHelpDeskUnreadCount(res.total ?? 0);
      } catch { /* keep 0 */ }
      try {
        const personalApi = createInboxApi('personal');
        const res = await personalApi.getThreads({ status: 'open', limit: 1, countOnly: 1 } as any);
        setInboxUnreadCount(res.total ?? 0);
      } catch { /* keep 0 */ }
      try {
        // Perf 3f: the server answers countOnly with one indexed COUNT(*)
        // instead of scanning + JSON-parsing every row to be length'd here.
        const res = await usersApi.getMyTickets({ countOnly: 1 } as any);
        setMyTicketsCount((res as any).count ?? (res.items ?? []).length);
      } catch { /* keep 0 */ }
      try {
        // Entry 197: unread form-submission alerts → the paprika Forms badge.
        const res = await formsApi.submissionsUnreadCount();
        setFormsUnreadCount(res?.data?.count ?? 0);
      } catch { /* keep 0 */ }
    };
    loadCounts();
    const interval = setInterval(loadCounts, 60000);
    return () => clearInterval(interval);
  }, []);
  const location = useLocation();
  const navigate = useNavigate();
  const { department: urlDepartment, redirect: initialRedirect } = parsePathname(location.pathname);
  const [selectedDepartment, setSelectedDepartment] = useState<string>(urlDepartment);

  // Redirect bare "/" to the user's home page so the URL stays correct
  useEffect(() => {
    if (location.pathname === '/') {
      const { department } = parsePathname('/');
      const target = departmentToPath(department);
      if (target !== '/') navigate(target, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle legacy URL redirects (e.g. /operations/clients → /clients)
  useEffect(() => {
    const { redirect } = parsePathname(location.pathname);
    // Carry the query string (S1-c1): the digest emails deep-link
    // /reports/scorecards?metric=<id>; a legacy move must never strip it.
    if (redirect) navigate(redirect + location.search, { replace: true });
  }, [location.pathname, location.search, navigate]);

  // Entry 542 (REVIEW-2): once the app is idle, fetch every lazy page's code in the
  // background, so a click draws the page at once (no spinner) and a tab opened
  // before a deploy still holds its pages.
  useEffect(() => { preloadLazyPages(); }, []);

  // Keep selectedDepartment in sync when the URL changes (back/forward, programmatic nav).
  // A LAYOUT effect (Entry 541): the page switches before the screen paints, so the old
  // page is never drawn against the new address (that frame was the "refresh" Michael saw,
  // and it let Bookings read the next page's address and pull him back).
  const prevPathRef = useRef(location.pathname);
  useLayoutEffect(() => {
    if (location.pathname !== prevPathRef.current) {
      prevPathRef.current = location.pathname;
      const { department } = parsePathname(location.pathname);
      setSelectedDepartment(department);
    }
    const p = location.pathname;
    if (
      p.startsWith('/reports') ||
      p.startsWith('/time-reports') ||
      p.startsWith('/call-intelligence') ||
      p.startsWith('/contacts') ||
      p.startsWith('/clients') ||
      p.startsWith('/templates')
    ) {
      setGroupOpen((prev) => ({ ...prev, Data: true }));
    }
  }, [location.pathname]);

  useEffect(() => {
    debugService.logRouteChange(location.pathname, location.search);
  }, [location.pathname, location.search]);

  const pathParsedLive = parsePathname(location.pathname);
  const reportSubPath = pathParsedLive.department === 'Reports' ? pathParsedLive.subPath : '';
  const showScorecardsChrome =
    selectedDepartment === 'Scorecards' || (selectedDepartment === 'Reports' && reportSubPath === 'scorecards');

  const [undoSnackbarOpen, setUndoSnackbarOpen] = useState(false);
  const [undoMetricId, setUndoMetricId] = useState<number | null>(null);
  const [undoMetricName, setUndoMetricName] = useState<string>('');
  const [clockifyProjectsWithTime, setClockifyProjectsWithTime] = useState<Array<{
    projectId: string;
    projectName: string;
    onCallHours: number;
    implementationHours: number;
    otherHours: number;
    supportHours: number;
    documentingFlowchartingHours: number;
    totalHours: number;
  }>>([]);
  const [clockifyLoading, setClockifyLoading] = useState(false);
  const [clockifyError, setClockifyError] = useState<string | null>(null);
  const [clockifyUsers, setClockifyUsers] = useState<Array<{ id: string; name: string }>>([]);
  const [clockifyProjects, setClockifyProjects] = useState<Array<{ id: string; name: string }>>([]);
  const [clockifySavedViews, setClockifySavedViews] = useState<Array<{ id: number; name: string; filters: ClockifyFilters; position: number; created_at: string }>>([]);
  const [myTimeSavedViews, setMyTimeSavedViews] = useState<Array<{ id: number; name: string; filters: ClockifyFilters; position: number; user_id?: number | null; created_at: string }>>([]);
  const [myTimeActiveView, setMyTimeActiveView] = useState<'all' | 'custom' | number>('all');
  const [myTimeCustomFilters, setMyTimeCustomFilters] = useState<ClockifyFilters>({});
  const [clockifyActiveView, setClockifyActiveView] = useState<'all' | 'custom' | number>('all');
  const [clockifyCustomFilters, setClockifyCustomFilters] = useState<ClockifyFilters>({});
  const [clockifySaveViewDialogOpen, setClockifySaveViewDialogOpen] = useState(false);
  const [clockifySaveViewName, setClockifySaveViewName] = useState('');
  const [clockifySaveViewMode, setClockifySaveViewMode] = useState<'new' | 'update'>('new');
  const [clockifySaveViewTargetId, setClockifySaveViewTargetId] = useState<number | null>(null);
  const [clockifyProjectsFilterOpen, setClockifyProjectsFilterOpen] = useState(false);
  const [clockifySettings, setClockifySettings] = useState<{
    pph_on_call: number | null;
    pph_off_call: number | null;
    operating_costs: number | null;
    active_clients: number | null;
    team_capacity_hours: number | null;
  } | null>(null);
  const [clockifyLaborCosts, setClockifyLaborCosts] = useState<Map<string, { laborCostOnCall: number; laborCostOffCall: number }>>(new Map());
  const [clockifyProjectMRRs, setClockifyProjectMRRs] = useState<Map<string, number>>(new Map());
  const [clockifyProjectBudgets, setClockifyProjectBudgets] = useState<Map<string, { budgeted_on_call: number | null; budgeted_off_call: number | null }>>(new Map());
  const [clockifyUserProjectHours, setClockifyUserProjectHours] = useState<Array<{ userId: string; userName: string; projectId: string; projectName: string; onCallHours: number; offCallHours: number; totalHours: number; implementationHours: number; supportHours: number; documentingHours: number; otherHours: number }>>([]);
  const [clockifyPeriodDays, setClockifyPeriodDays] = useState<number | null>(null);
  const [reportDataSource, setReportDataSource] = useState<ReportDataSource>('merged');
  const [clockifyDataRefreshTrigger] = useState(0);
  const [editingMrrProjectId, setEditingMrrProjectId] = useState<string | null>(null);
  const [editingMrrValue, setEditingMrrValue] = useState<string>('');
  const [editingBudgetProjectId, setEditingBudgetProjectId] = useState<string | null>(null);
  const [editingBudgetField, setEditingBudgetField] = useState<'on' | 'off' | null>(null);
  const [editingBudgetValue, setEditingBudgetValue] = useState<string>('');
  const [utilizationPanelOpen, setUtilizationPanelOpen] = useState(true);
  const [clockifyPortfolioClients, setClockifyPortfolioClients] = useState<Map<string, { card_id: string; project_manager: string | null; implementer: string | null; expected_wrap_up: string | null; stage: string | null }>>(new Map());
  const [editingWrapUpCardId, setEditingWrapUpCardId] = useState<string | null>(null);
  const [editingWrapUpValue, setEditingWrapUpValue] = useState<string>('');
  const [clockifySortColumn, setClockifySortColumn] = useState<string>('projectName');
  const [clockifySortDirection, setClockifySortDirection] = useState<'asc' | 'desc'>('asc');

  // ---------- Drill-down dialog ----------
  const [drillDownOpen, setDrillDownOpen] = useState(false);
  const [drillDownMetricId, setDrillDownMetricId] = useState<number | null>(null);
  const [drillDownMetricName, setDrillDownMetricName] = useState('');
  const [drillDownWeek, setDrillDownWeek] = useState('');
  const [drillDownUnit, setDrillDownUnit] = useState<string | null>(null);

  // ---------- Metric annotations ----------
  const [annotationsByMetric, setAnnotationsByMetric] = useState<Record<number, Array<{ id: number; weekStart: string; note: string }>>>({});
  const [annotationPopover, setAnnotationPopover] = useState<{ anchorEl: HTMLElement; metricId: number; metricName: string; weekStart: string } | null>(null);
  const [annotationNoteInput, setAnnotationNoteInput] = useState('');
  const [annotationSaving, setAnnotationSaving] = useState(false);

  const getAnnotationForCell = (metricId: number, weekStart: string) =>
    annotationsByMetric[metricId]?.find(a => a.weekStart === weekStart);

  const handleSaveAnnotation = async () => {
    if (!annotationPopover || !annotationNoteInput.trim()) return;
    setAnnotationSaving(true);
    try {
      const existing = getAnnotationForCell(annotationPopover.metricId, annotationPopover.weekStart);
      if (existing) {
        await metricsApi.deleteAnnotation(existing.id);
      }
      await metricsApi.createAnnotation(annotationPopover.metricId, { week_start: annotationPopover.weekStart, note: annotationNoteInput.trim() });
      const updated = await metricsApi.getAnnotations(annotationPopover.metricId);
      setAnnotationsByMetric(prev => ({ ...prev, [annotationPopover.metricId]: updated }));
      setAnnotationPopover(null);
      setAnnotationNoteInput('');
    } catch { /* ignore */ }
    setAnnotationSaving(false);
  };

  const handleDeleteAnnotation = async () => {
    if (!annotationPopover) return;
    const existing = getAnnotationForCell(annotationPopover.metricId, annotationPopover.weekStart);
    if (!existing) return;
    try {
      await metricsApi.deleteAnnotation(existing.id);
      const updated = await metricsApi.getAnnotations(annotationPopover.metricId);
      setAnnotationsByMetric(prev => ({ ...prev, [annotationPopover.metricId]: updated }));
      setAnnotationPopover(null);
      setAnnotationNoteInput('');
    } catch { /* ignore */ }
  };

  const openDrillDown = (metricId: number, metricName: string, weekStart: string, unit?: string | null) => {
    setDrillDownMetricId(metricId);
    setDrillDownMetricName(metricName);
    setDrillDownWeek(weekStart);
    setDrillDownUnit(unit || null);
    setDrillDownOpen(true);
  };

  // ---------- Sparkline detail dialog ----------
  const [sparklineDetailOpen, setSparklineDetailOpen] = useState(false);
  const [sparklineDetailMetricId, setSparklineDetailMetricId] = useState<number>(0);
  const [sparklineDetailMetricName, setSparklineDetailMetricName] = useState('');
  const [sparklineDetailUnit, setSparklineDetailUnit] = useState<string | null>(null);

  const openSparklineDetail = (metricId: number, metricName: string, unit?: string | null) => {
    setSparklineDetailMetricId(metricId);
    setSparklineDetailMetricName(metricName);
    setSparklineDetailUnit(unit || null);
    setSparklineDetailOpen(true);
  };

  // ---------- Column visibility + ordering ----------
  const CLOCKIFY_ALL_COLUMNS: Array<{ id: string; label: string; group: string }> = [
    { id: 'projectName', label: 'Clients', group: 'Core' },
    { id: 'pm', label: 'PM', group: 'Team' },
    { id: 'implementer', label: 'Implementer', group: 'Team' },
    { id: 'wrapUp', label: 'Wrap-Up', group: 'Team' },
    { id: 'onCallHours', label: 'On Call', group: 'Hours' },
    { id: 'implementationHours', label: 'Implementation', group: 'Hours' },
    { id: 'otherHours', label: 'Other', group: 'Hours' },
    { id: 'supportHours', label: 'Support', group: 'Hours' },
    { id: 'documentingFlowchartingHours', label: 'Documenting', group: 'Hours' },
    { id: 'onOffRatio', label: 'On:Off Call', group: 'Hours' },
    { id: 'totalHours', label: 'Total', group: 'Hours' },
    { id: 'mrr', label: 'MRR', group: 'Financial' },
    { id: 'cost', label: 'Cost', group: 'Financial' },
    { id: 'clientProfit', label: 'Client Profit', group: 'Financial' },
    { id: 'hourlyMargin', label: 'Hourly Margin', group: 'Financial' },
    { id: 'budgetOn', label: 'Budget On', group: 'Efficiency' },
    { id: 'budgetOff', label: 'Budget Off', group: 'Efficiency' },
    { id: 'utilOn', label: 'Util % On', group: 'Efficiency' },
    { id: 'utilOff', label: 'Util % Off', group: 'Efficiency' },
    { id: 'revPerHr', label: 'Rev/Hr', group: 'Financial' },
  ];
  const CLOCKIFY_DEFAULT_COLUMNS = CLOCKIFY_ALL_COLUMNS.map(c => c.id);
  const [clockifyVisibleColumns, setClockifyVisibleColumns] = useState<string[]>(CLOCKIFY_DEFAULT_COLUMNS);
  const [columnEditorAnchor, setColumnEditorAnchor] = useState<null | HTMLElement>(null);

  // ---------- Forecasting state ----------

  // ---------- Finances / P&L state ----------

  // -- Scorecard Saved Views --
  const [scorecardSavedViews, setScorecardSavedViews] = useState<Array<{ id: number; name: string; filters: ScorecardViewFilters; position: number; created_at: string }>>([]);
  const [scorecardActiveView, setScorecardActiveView] = useState<'all' | 'custom' | number>('all');
  const [scorecardCustomFilters, setScorecardCustomFilters] = useState<ScorecardViewFilters>({});
  const [scorecardSaveDialogOpen, setScorecardSaveDialogOpen] = useState(false);
  const [scorecardSaveViewName, setScorecardSaveViewName] = useState('');
  const [scorecardSaveViewMode, setScorecardSaveViewMode] = useState<'new' | 'update'>('new');
  const [scorecardSaveViewTargetId, setScorecardSaveViewTargetId] = useState<number | null>(null);
  const [scorecardPeopleList, setScorecardPeopleList] = useState<string[]>([]);
  const [scorecardSelectedPeople, setScorecardSelectedPeople] = useState<string[]>([]);

  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({
    metric: 200,
    summary: 140,
    pace: 160,
    defaultWeek: 110,
  });
  const [resizing, setResizing] = useState<{
    key: string;
    startX: number;
    startWidth: number;
  } | null>(null);
  // Metric edit/delete moved to Settings page
  const [, setDashboardView] = useState<'base' | 'custom'>('base');
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [activeDashboardId, setActiveDashboardId] = useState<number | null>(null);
  const [departments, setDepartments] = useState<{ id: number; name: string }[]>([]);
  const [visibilityDialogOpen, setVisibilityDialogOpen] = useState(false);
  const [hiddenMetricIds, setHiddenMetricIds] = useState<Set<number>>(new Set());
  const [, setCustomDashboardMetricIds] = useState<number[]>([]);
  const [createDashboardDialogOpen, setCreateDashboardDialogOpen] = useState(false);
  const [createDashboardDialogDepartmentId, setCreateDashboardDialogDepartmentId] = useState<number | null>(null);
  const [editDashboardDialogOpen, setEditDashboardDialogOpen] = useState(false);

  // Listen for command palette navigation events (legacy custom-event bridge)
  useEffect(() => {
    const handler = (e: Event) => {
      const section = (e as CustomEvent).detail?.section;
      if (section) {
        navigate(departmentToPath(section));
        setSelectedDepartment(section);
      }
    };
    window.addEventListener('bsg-navigate', handler);
    return () => window.removeEventListener('bsg-navigate', handler);
  }, [navigate]);

  useEffect(() => {
    const loadData = async () => {
      const hiddenIds = await loadVisibility();
      await loadScorecard(hiddenIds);
    };
    if (showScorecardsChrome || selectedDepartment === 'My Scorecard' || departments.length > 0) {
      loadData();
    } else {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quarter, year, dateFilterStart, dateFilterEnd, refreshSignal, selectedDepartment, departments, scorecardSelectedPeople, showScorecardsChrome, reportSubPath]);

  useEffect(() => {
    loadDepartments();
  }, []);

  useEffect(() => {
    loadDashboards();
  }, []);

  // Visibility is now loaded as part of the scorecard loading process

  const loadDashboards = async () => {
    try {
      // Always load all dashboards so the left menu is the same regardless of department
      const list = await dashboardsApi.list({
        departmentId: null,
        includeMixed: true,
      });
      setDashboards(list);
      // If current dashboard was deleted, clear selection
      setActiveDashboardId((currentId) => {
        if (!currentId) return currentId;
        if (list.some((d) => d.id === currentId)) return currentId;
        return null;
      });
    } catch (error) {
      console.error('Failed to load dashboards:', error);
    }
  };

  const loadDepartments = async () => {
    try {
      const list = await departmentsApi.getAll();
      setDepartments(list);
    } catch (error) {
      console.error('Failed to load departments:', error);
    }
  };

  const loadVisibility = async (): Promise<Set<number>> => {
    try {
      const departmentId =
        showScorecardsChrome
          ? null
          : departments.find((dept) => dept.name === selectedDepartment)?.id || null;
      const isMixed = showScorecardsChrome;
      const dashboardId: number | null = null;

      const visibility = await metricVisibilityApi.get({
        dashboardId,
        departmentId,
        isMixed,
      });

      // Only include metrics that are explicitly hidden (hidden === true)
      // Metrics without a visibility record or with hidden === false should be visible
      const hiddenIds = new Set(
        visibility.filter((v) => v.hidden === true).map((v) => v.metric_id)
      );
      setHiddenMetricIds(hiddenIds);
      return hiddenIds;
    } catch (error) {
      console.error('Failed to load visibility:', error);
      return new Set<number>();
    }
  };

  const _handleOpenCreateDashboardDialog = async () => {
    let departmentId: number | null = null;
    if (!showScorecardsChrome) {
      departmentId = departments.find((d) => d.name === selectedDepartment)?.id ?? null;
      if (!departmentId) {
        const refreshed = await departmentsApi.getAll();
        setDepartments(refreshed);
        departmentId = refreshed.find((d) => d.name === selectedDepartment)?.id ?? null;
      }
      if (!departmentId) {
        const createdDept = await departmentsApi.create(selectedDepartment);
        setDepartments((prev) => [...prev, createdDept]);
        departmentId = createdDept.id;
      }
    }
    setCreateDashboardDialogDepartmentId(departmentId);
    setCreateDashboardDialogOpen(true);
  };

  const handleCreateDashboardSuccess = (created: Dashboard) => {
    setDashboards((prev) => [...prev, created]);
    setActiveDashboardId(created.id);
    setDashboardView('custom');
  };

  const _handleDeleteDashboard = (dashboardId: number) => {
    const target = dashboards.find((d) => d.id === dashboardId);
    if (!target) return;
    showConfirm({
      message: `Delete "${target.name}"? This will remove its widgets.`,
      variant: 'danger',
      confirmLabel: 'Delete',
      onConfirm: async () => {
        try {
          await dashboardsApi.delete(dashboardId);
          const remaining = dashboards.filter((d) => d.id !== dashboardId);
          setDashboards(remaining);
          if (remaining.length > 0) {
            setActiveDashboardId(remaining[0].id);
            setDashboardView('custom');
          } else {
            setActiveDashboardId(null);
            setDashboardView('base');
          }
        } catch (error) {
          console.error('Failed to delete dashboard:', error);
        }
      },
    });
  };

  useEffect(() => {
    if (!resizing) return;
    const handleMove = (event: MouseEvent) => {
      const delta = event.clientX - resizing.startX;
      const nextWidth = Math.max(70, resizing.startWidth + delta);
      setColumnWidths((prev) => ({
        ...prev,
        [resizing.key]: nextWidth,
      }));
    };
    const handleUp = () => setResizing(null);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [resizing]);

  const NON_SCORECARD_DEPARTMENTS = new Set([
    'Dashboard', 'Time Reports', 'Finances', 'Settings', 'My Tickets',
    'My Time', 'Time Tracking', 'Insights', 'Call Intelligence', 'Sales',
    'EOS', 'Inbox', 'Reports', 'Marketing', 'Mental Herd', 'Phones',
    'Proposals',
  ]);

  // GET /api/scorecard is gated tab:scorecards | tab:eos (SEC-W1). A role
  // holding neither must not fetch it — and must not have every non-exempt
  // department walled off by the shell-level loading/error/empty guards that
  // sit above the department dispatch (the 2026-07-31 /templates 403 wall).
  // Reads useAuth() directly: the component's `auth` const is declared further
  // down, after this predicate's declaration site (TS2448 otherwise).
  const { hasTab: hasScorecardTab } = useAuth();
  const canReadScorecard = hasScorecardTab('scorecards') || hasScorecardTab('eos');

  const loadScorecard = async (hiddenIds?: Set<number>) => {
    const skipScorecardLoad =
      !canReadScorecard ||
      (NON_SCORECARD_DEPARTMENTS.has(selectedDepartment) &&
        !(selectedDepartment === 'Reports' && reportSubPath === 'scorecards'));
    if (skipScorecardLoad) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);

      // Use provided hiddenIds or current state
      const idsToFilter = hiddenIds ?? hiddenMetricIds;

      // Load quarter info only if not using date filters
      if (!dateFilterStart && !dateFilterEnd) {
        let info: QuarterInfo;
        if (quarter && year) {
          info = await targetsApi.getQuarterInfo(quarter, year);
        } else {
          info = await targetsApi.getCurrentQuarter();
        }
        setQuarterInfo(info);
      } else {
        setQuarterInfo(null); // Clear quarter info when using date filters
      }

      // Load scorecard data with optional date filters. For "My Scorecard"
      // (team member view), always filter by the user's mapped identity.
      const assigneeParam = selectedDepartment === 'My Scorecard' && auth.identities.aptly?.name
        ? auth.identities.aptly.name
        : undefined;
      // For Scorecards tab, pass person filter if active
      const assigneesParam = showScorecardsChrome && scorecardSelectedPeople.length > 0
        ? scorecardSelectedPeople : undefined;
      const data = await scorecardApi.get(
        quarter,
        year,
        dateFilterStart || undefined,
        dateFilterEnd || undefined,
        assigneeParam,
        assigneesParam
      );
      // Filter out hidden metrics - only show metrics that are NOT in the hidden set
      const visibleData = data.filter((metric) => !idsToFilter.has(metric.metric_id));
      setScorecard(visibleData);
      
      // If using date filters and we have data, extract weeks from the data
      if ((dateFilterStart || dateFilterEnd) && data.length > 0 && data[0].weeks.length > 0) {
        const weeksFromData = data[0].weeks.map(w => w.week_start);
        setQuarterInfo({
          quarter: quarter || 1,
          year: year || new Date().getFullYear(),
          start_date: dateFilterStart || '',
          end_date: dateFilterEnd || '',
          sundays: weeksFromData,
          week_count: weeksFromData.length,
        });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load scorecard');
    } finally {
      setLoading(false);
    }
  };

  const formatValue = (value: number | null | undefined, unit: string | null) => {
    if (value === null || value === undefined) return 'N/A';
    
    // Convert hours to HH:MM:SS format for Clockify metrics
    const cleanUnit = unit ? unit.replace(/^#+\s*|\s*#+$/g, '').trim().toLowerCase() : '';
    if ((cleanUnit === 'hours' || cleanUnit === 'hr' || cleanUnit === 'hrs') && typeof value === 'number') {
      const totalSeconds = Math.round(Math.abs(value) * 3600);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      const sign = value < 0 ? '-' : '';
      const formatted = `${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      
      const unitIcon = getUnitIcon(cleanUnit);
      if (unitIcon) {
        return (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <span>{formatted}</span>
            {unitIcon}
          </Box>
        );
      }
      return `${formatted} ${cleanUnit}`;
    }

    // Dollar formatting  -  symbol goes in front
    if (cleanUnit === '$' && typeof value === 'number') {
      if (value < 0) {
        return `-$${Math.abs(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      }
      return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    
    const formatted =
      typeof value === 'number' ? value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : value;
    if (!unit) return formatted;

    const unitIcon = getUnitIcon(cleanUnit);
    if (!unitIcon) {
      return cleanUnit ? `${formatted} ${cleanUnit}` : formatted;
    }
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        <span>{formatted}</span>
        {unitIcon}
      </Box>
    );
  };

  const getUnitIcon = (unit: string) => {
    if (!unit) return null;
    if (['hour', 'hours', 'hr', 'hrs'].includes(unit)) {
      return <AccessTimeIcon fontSize="small" />;
    }
    if (['ticket', 'tickets'].includes(unit)) {
      return <LocalOfferIcon fontSize="small" sx={{ transform: 'rotate(-45deg)', color: 'text.secondary' }} />;
    }
    if (unit === '%' || unit === 'percent' || unit === 'percentage') {
      return <PercentIcon fontSize="small" />;
    }
    return null;
  };

  const getMetricSourceLabel = (metric: { description: string | null; source_type?: string | null }) => {
    if (metric.source_type === 'api') return 'Source: API Sync';
    if (!metric.description) return 'Source: Manual';
    const lower = metric.description.toLowerCase();
    if (lower.includes('imported')) return 'Source: API Sync';
    return 'Source: Manual';
  };

  const isApiMetric = (metric: { description: string | null; source_type?: string | null }) => {
    if (metric.source_type === 'api' || metric.source_type === 'csv') return true;
    if (!metric.description) return false;
    const lower = metric.description.toLowerCase();
    return lower.includes('imported') || lower.includes('aptly') || lower.includes('api');
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatCompactDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return `${month}/${day}`;
  };

  // Week labels: weeks run Mon - Sun, the key date is the Monday
  const formatWeekLabel = (mondayStr: string) => {
    const monday = new Date(mondayStr);
    const sunday = new Date(monday);
    sunday.setDate(sunday.getDate() + 6);
    return `Week of ${formatDate(mondayStr)} - ${formatDate(sunday.toISOString().split('T')[0])}`;
  };

  const formatWeekLabelForWidth = (mondayStr: string, width: number) => {
    const monday = new Date(mondayStr);
    const sunday = new Date(monday);
    sunday.setDate(sunday.getDate() + 6);
    const start = formatCompactDate(mondayStr);
    const end = formatCompactDate(sunday.toISOString().split('T')[0]);
    if (width < 90) {
      return `${start}-${end}`;
    }
    if (width < 120) {
      return `Wk ${start}-${end}`;
    }
    return formatWeekLabel(mondayStr);
  };

  const getColumnWidth = (key: string) => {
    if (key in columnWidths) return columnWidths[key];
    return columnWidths.defaultWeek;
  };

  const startResize = (key: string, event: React.MouseEvent) => {
    event.preventDefault();
    setResizing({
      key,
      startX: event.clientX,
      startWidth: getColumnWidth(key),
    });
  };

  const getCurrentWeekKey = (weekStarts: string[]) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let currentWeek: string | null = null;
    for (const weekStart of weekStarts) {
      const start = new Date(weekStart);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      if (today >= start && today <= end) {
        currentWeek = weekStart;
        break;
      }
      if (today >= start) {
        currentWeek = weekStart;
      }
    }
    return currentWeek;
  };

  const isCurrentQuarterView = () => {
    if (!quarterInfo) return false;
    const now = new Date();
    const month = now.getMonth();
    const currentQuarter = Math.floor(month / 3) + 1;
    return quarterInfo.quarter === currentQuarter && quarterInfo.year === now.getFullYear();
  };

  const auth = useAuth();
  const toast = useToast();
  const hasAnyCompanyTab = auth.hasTab('sales') || auth.hasTab('operations') || auth.hasTab('marketing') || auth.hasTab('finances') || auth.hasTab('eos');
  const isPersonalView = auth.isTeamMember() && !hasAnyCompanyTab;

  // Set default tab for team members on first load
  useEffect(() => {
    if (isPersonalView && selectedDepartment === 'Scorecards') {
      navigate('/my-week', { replace: true });
      setSelectedDepartment('My Week');
    }
  }, [isPersonalView]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load My Day summary when viewing My Week
  useEffect(() => {
    if (selectedDepartment !== 'My Week') return;
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today.getTime() + 86400000);
    timeTrackingApi.getSummary({
      startDate: today.toISOString(),
      endDate: tomorrow.toISOString(),
      userId: auth.user?.id,
    }).then(res => { if (res.success) setMyDaySummary(res.data); }).catch(() => {});
  }, [selectedDepartment, auth.user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const allDepartmentOptions = isPersonalView
    ? ['My Week', 'My Scorecard', 'My Tickets', 'My Time']
    : [
        'Scorecards', 'Insights', 'Sales', 'Operations', 'Marketing', 'Finances',
        'EOS', 'Engineering', 'Time Tracking', 'Phones', 'Time Reports',
        'Call Intelligence', 'Contacts', 'Clients', 'Reports',
        'Templates', 'Scheduling',
      ];
  const departmentOptions = isPersonalView ? allDepartmentOptions : allDepartmentOptions;

  // rail v2: the company/data groupings moved into config/navTree.ts; the
  // resolver owns which items render. departmentOptions still drives the
  // personal-view rail + the page-title chrome.
  const departmentIconMap: Record<string, React.ReactNode> = {
    Dashboard: <DashboardIcon fontSize="small" />,
    Scorecards: <DashboardIcon fontSize="small" />,
    Insights: <InsightsIcon fontSize="small" />,
    Sales: <WorkOutlineIcon fontSize="small" />,
    Operations: <FactoryIcon fontSize="small" />,
    Marketing: <BarChartIcon fontSize="small" />,
    Finances: <AttachMoneyIcon fontSize="small" />,
    'Time Reports': <ScheduleIcon fontSize="small" />,
    'Time Tracking': <TimerIcon fontSize="small" />,
    Comms: <ForumIcon fontSize="small" />,
    Forecasting: <WbSunnyIcon fontSize="small" />,
    'Call Intelligence': <PhoneInTalkIcon fontSize="small" />,
    Proposals: <RequestQuoteIcon fontSize="small" />,
    EOS: <AccountTreeIcon fontSize="small" />,
    Meetings: <GroupsIcon fontSize="small" />,
    Engineering: <BuildIcon fontSize="small" />,
    Contacts: <ContactsIcon fontSize="small" />,
    Clients: <BusinessIcon fontSize="small" />,
    Reports: <AssessmentIcon fontSize="small" />,
    Templates: <DescriptionIcon fontSize="small" />,
    Scheduling: <CalendarMonthIcon fontSize="small" />,
    Forms: <DynamicFormIcon fontSize="small" />,
    'Documentation Nation': <AccountTreeIcon fontSize="small" />,
    'Mental Herd': <FenceIcon fontSize="small" />,
    Settings: <SettingsIcon fontSize="small" />,
    PersonalInbox: <MailOutlineIcon fontSize="small" />,
    HelpDesk: <MoveToInboxIcon fontSize="small" />,
    // rail v2 iconKeys (config/navTree.ts)
    Email: <MailOutlineIcon fontSize="small" />,
    Todos: <ChecklistIcon fontSize="small" />,
    Rocks: <FlagIcon fontSize="small" />,
    IDS: <AssignmentIcon fontSize="small" />,
    Projects: <ViewKanbanIcon fontSize="small" />,
    Tickets: <ConfirmationNumberIcon fontSize="small" />,
    'AI HR': <SmartToyIcon fontSize="small" />,
    'My Week': <DashboardIcon fontSize="small" />,
    'My Scorecard': <PersonIcon fontSize="small" />,
    'My Tickets': <AssignmentIcon fontSize="small" />,
    'My Time': <TimerIcon fontSize="small" />,
    SmokeTests: <ChecklistIcon fontSize="small" />,
    AiMissionControl: <PsychologyIcon fontSize="small" />,
  };

  const handleSelectDepartment = useCallback((department: string) => {
    navigate(departmentToPath(department));
    setSelectedDepartment(department);
  }, [navigate]);

  // rail v2: the nav tree + per-item visibility live in config/navTree.ts. We
  // pass a thin auth slice as the gate context, resolve the visible tree, and
  // render whatever it returns (hide-don't-lock: absent items never reach DOM).
  // These hooks must sit above the component's scorecard early-returns below.
  const navCtx = useMemo(() => ({
    hasTab: (t: string) => auth.hasTab(t),
    hasAction: (a: string) => auth.hasAction(a),
    isAdmin: () => auth.isAdmin(),
    isTeamMember: () => auth.isTeamMember(),
  }), [auth]);
  const handleNavClick = useCallback((item: NavItem) => {
    if (item.comingSoon) return;
    navigate(item.path);
  }, [navigate]);


  // Load users, projects, and saved views when viewing Time Reports or My Time
  useEffect(() => {
    if (selectedDepartment !== 'Time Reports' && selectedDepartment !== 'My Time') return;
    timeTrackingApi.getUsers().then((res) => {
      if (res.success && Array.isArray(res.data)) {
        setClockifyUsers(res.data.map((u: { id: number; display_name: string; email: string }) => ({ id: String(u.id), name: u.display_name || u.email })));
      }
    }).catch(() => setClockifyUsers([]));
    timeTrackingApi.getProjects().then((res) => {
      if (res.success && Array.isArray(res.data)) {
        setClockifyProjects(res.data.map((p: { id: number; name: string }) => ({ id: String(p.id), name: p.name })));
      }
    }).catch(() => setClockifyProjects([]));
    timeReportsApi.getSavedViews().then((res) => {
      if (res.success && Array.isArray(res.data)) setClockifySavedViews(res.data);
    }).catch(() => setClockifySavedViews([]));
  }, [selectedDepartment, refreshSignal]);

  // Load personal saved views for My Time
  useEffect(() => {
    if (selectedDepartment !== 'My Time') return;
    const userId = auth.user?.id;
    if (!userId) return;
    timeReportsApi.getSavedViews().then((res) => {
      if (res.success && Array.isArray(res.data)) {
        setMyTimeSavedViews(res.data.filter(v => v.user_id === userId));
      }
    }).catch(() => setMyTimeSavedViews([]));
  }, [selectedDepartment, refreshSignal, auth.user?.id]);

  // Load scorecard saved views and people list when on Scorecards tab
  useEffect(() => {
    if (!showScorecardsChrome) return;
    settingsApi.getScorecardViews().then((res) => {
      if (res.success && Array.isArray(res.data)) setScorecardSavedViews(res.data);
    }).catch(() => setScorecardSavedViews([]));
    settingsApi.getPeople().then((res) => {
      if (res.success && Array.isArray(res.data)) setScorecardPeopleList(res.data);
    }).catch(() => setScorecardPeopleList([]));
  }, [selectedDepartment, refreshSignal, showScorecardsChrome]);

  // Effective scorecard view filters (merge in selected people)
  const scorecardEffectiveFilters: ScorecardViewFilters = useMemo(() => {
    const base =
      scorecardActiveView === 'custom'
        ? scorecardCustomFilters
        : scorecardActiveView === 'all'
          ? {}
          : (scorecardSavedViews.find((v) => v.id === scorecardActiveView)?.filters ?? {});
    return {
      ...base,
      ...(scorecardSelectedPeople.length > 0 ? { assignees: scorecardSelectedPeople } : {}),
    };
  }, [scorecardActiveView, scorecardCustomFilters, scorecardSavedViews, scorecardSelectedPeople]);

  // Current filters for the active view (uses personal views when in My Time)
  const clockifyEffectiveFilters = useMemo(() => selectedDepartment === 'My Time'
    ? (myTimeActiveView === 'custom'
        ? myTimeCustomFilters
        : myTimeActiveView === 'all'
          ? {}
          : (myTimeSavedViews.find((v) => v.id === myTimeActiveView)?.filters ?? {}))
    : (clockifyActiveView === 'custom'
        ? clockifyCustomFilters
        : clockifyActiveView === 'all'
          ? {}
          : (clockifySavedViews.find((v) => v.id === clockifyActiveView)?.filters ?? {})),
    [selectedDepartment, myTimeActiveView, myTimeCustomFilters, myTimeSavedViews, clockifyActiveView, clockifyCustomFilters, clockifySavedViews]);

  // Memoize sorted Clockify rows to avoid expensive filter+sort on every render
  const clockifySortedRows = useMemo(() => {
    const filtered = clockifyEffectiveFilters.hideZeroTime
      ? clockifyProjectsWithTime.filter((row) => row.totalHours > 0)
      : clockifyProjectsWithTime;
    const AVG_DAYS_SORT = 30.44;
    const getSortValue = (row: typeof filtered[0]): number | string => {
      const col = clockifySortColumn;
      if (col === 'projectName') return row.projectName.toLowerCase();
      if (col === 'pm') {
        const pf = clockifyPortfolioClients.get(row.projectName.toLowerCase().replace(/[^a-z0-9]/g, ''));
        return (pf?.project_manager ?? '').toLowerCase();
      }
      if (col === 'implementer') {
        const pf = clockifyPortfolioClients.get(row.projectName.toLowerCase().replace(/[^a-z0-9]/g, ''));
        return (pf?.implementer ?? '').toLowerCase();
      }
      if (col === 'wrapUp') {
        const pf = clockifyPortfolioClients.get(row.projectName.toLowerCase().replace(/[^a-z0-9]/g, ''));
        return pf?.expected_wrap_up ?? 'zzzz';
      }
      if (col === 'onCallHours') return row.onCallHours;
      if (col === 'implementationHours') return row.implementationHours;
      if (col === 'otherHours') return row.otherHours;
      if (col === 'supportHours') return row.supportHours;
      if (col === 'documentingFlowchartingHours') return row.documentingFlowchartingHours ?? 0;
      if (col === 'totalHours') return row.totalHours;
      if (col === 'onOffRatio') {
        const offCall = row.implementationHours + row.otherHours + row.supportHours + (row.documentingFlowchartingHours ?? 0);
        return offCall > 0 ? row.onCallHours / offCall : (row.onCallHours > 0 ? 999 : 0);
      }
      if (col === 'mrr') return clockifyProjectMRRs.get(row.projectId) ?? 0;
      if (col === 'cost') {
        const lab = clockifyLaborCosts.get(row.projectId) ?? { laborCostOnCall: 0, laborCostOffCall: 0 };
        const monthlyOp = clockifySettings?.operating_costs ?? 0;
        const numClients = clockifySettings?.active_clients ?? 1;
        const monthlyOpPerClient = numClients > 0 ? monthlyOp / numClients : 0;
        const proratedOp = clockifyPeriodDays != null ? monthlyOpPerClient * (clockifyPeriodDays / AVG_DAYS_SORT) : monthlyOpPerClient;
        return lab.laborCostOnCall + lab.laborCostOffCall + proratedOp;
      }
      if (col === 'clientProfit') {
        const monthlyMrr = clockifyProjectMRRs.get(row.projectId) ?? 0;
        const proratedMrr = clockifyPeriodDays != null ? monthlyMrr * (clockifyPeriodDays / AVG_DAYS_SORT) : monthlyMrr;
        const lab = clockifyLaborCosts.get(row.projectId) ?? { laborCostOnCall: 0, laborCostOffCall: 0 };
        const monthlyOp = clockifySettings?.operating_costs ?? 0;
        const numClients = clockifySettings?.active_clients ?? 1;
        const monthlyOpPerClient = numClients > 0 ? monthlyOp / numClients : 0;
        const proratedOp = clockifyPeriodDays != null ? monthlyOpPerClient * (clockifyPeriodDays / AVG_DAYS_SORT) : monthlyOpPerClient;
        return proratedMrr - (lab.laborCostOnCall + lab.laborCostOffCall + proratedOp);
      }
      if (col === 'hourlyMargin') {
        const pphOn = clockifySettings?.pph_on_call ?? 0;
        const pphOff = clockifySettings?.pph_off_call ?? 0;
        const offCallHrs = row.implementationHours + row.otherHours + row.supportHours + (row.documentingFlowchartingHours ?? 0);
        const pphRevenue = (pphOn * row.onCallHours) + (pphOff * offCallHrs);
        const lab = clockifyLaborCosts.get(row.projectId) ?? { laborCostOnCall: 0, laborCostOffCall: 0 };
        const monthlyOp = clockifySettings?.operating_costs ?? 0;
        const numClients = clockifySettings?.active_clients ?? 1;
        const monthlyOpPerClient = numClients > 0 ? monthlyOp / numClients : 0;
        const proratedOp = clockifyPeriodDays != null ? monthlyOpPerClient * (clockifyPeriodDays / AVG_DAYS_SORT) : monthlyOpPerClient;
        return pphRevenue - (lab.laborCostOnCall + lab.laborCostOffCall + proratedOp);
      }
      if (col === 'budgetOn') return clockifyProjectBudgets.get(row.projectId)?.budgeted_on_call ?? -1;
      if (col === 'budgetOff') return clockifyProjectBudgets.get(row.projectId)?.budgeted_off_call ?? -1;
      if (col === 'utilOn') {
        const b = clockifyProjectBudgets.get(row.projectId)?.budgeted_on_call;
        return b != null && b > 0 ? (row.onCallHours / b) * 100 : -1;
      }
      if (col === 'utilOff') {
        const b = clockifyProjectBudgets.get(row.projectId)?.budgeted_off_call;
        const offCallHrs = row.implementationHours + row.otherHours + row.supportHours + (row.documentingFlowchartingHours ?? 0);
        return b != null && b > 0 ? (offCallHrs / b) * 100 : -1;
      }
      if (col === 'revPerHr') {
        const monthlyMrr = clockifyProjectMRRs.get(row.projectId) ?? 0;
        const proratedMrr = clockifyPeriodDays != null ? monthlyMrr * (clockifyPeriodDays / AVG_DAYS_SORT) : monthlyMrr;
        return row.totalHours > 0 && monthlyMrr > 0 ? proratedMrr / row.totalHours : -1;
      }
      return 0;
    };
    return [...filtered].sort((a, b) => {
      const va = getSortValue(a);
      const vb = getSortValue(b);
      const dir = clockifySortDirection === 'asc' ? 1 : -1;
      if (typeof va === 'string' && typeof vb === 'string') return va.localeCompare(vb) * dir;
      return ((va as number) - (vb as number)) * dir;
    });
  }, [
    clockifyEffectiveFilters.hideZeroTime,
    clockifyProjectsWithTime,
    clockifySortColumn,
    clockifySortDirection,
    clockifyPortfolioClients,
    clockifyProjectMRRs,
    clockifyLaborCosts,
    clockifySettings,
    clockifyPeriodDays,
    clockifyProjectBudgets,
  ]);

  // Sync column visibility when switching views (team or personal)
  useEffect(() => {
    const activeView = selectedDepartment === 'My Time' ? myTimeActiveView : clockifyActiveView;
    const customFilters = selectedDepartment === 'My Time' ? myTimeCustomFilters : clockifyCustomFilters;
    const savedViews = selectedDepartment === 'My Time' ? myTimeSavedViews : clockifySavedViews;
    if (activeView === 'all') {
      setClockifyVisibleColumns(CLOCKIFY_DEFAULT_COLUMNS);
    } else if (activeView === 'custom') {
      if (customFilters.columns) setClockifyVisibleColumns(customFilters.columns);
    } else {
      const view = savedViews.find((v) => v.id === activeView);
      if (view?.filters?.columns) setClockifyVisibleColumns(view.filters.columns);
      else setClockifyVisibleColumns(CLOCKIFY_DEFAULT_COLUMNS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- CLOCKIFY_DEFAULT_COLUMNS is a constant
  }, [clockifyActiveView, clockifySavedViews, myTimeActiveView, myTimeSavedViews, myTimeCustomFilters, clockifyCustomFilters, selectedDepartment]);

  // Ensure selected saved view still exists; if deleted or invalid (e.g. id 0), reset to All
  useEffect(() => {
    if (typeof clockifyActiveView === 'number') {
      const viewExists = clockifySavedViews.some((v) => v.id === clockifyActiveView);
      if (!viewExists || clockifyActiveView === 0) {
        setClockifyActiveView('all');
        setClockifyCustomFilters({});
      }
    }
  }, [clockifyActiveView, clockifySavedViews]);

  const hasClockifyFilters = (f: typeof clockifyCustomFilters) =>
    (f.userIds?.length ?? 0) > 0 || (f.projectIdsExcluded?.length ?? 0) > 0 || f.hideZeroTime === true ||
    (!!f.timeRange && (!!f.timeRange.preset || !!f.timeRange.startDate || !!f.timeRange.endDate));

  const getClockifyTimeRange = (tr: typeof clockifyCustomFilters.timeRange): { startDate?: string; endDate?: string } => {
    if (!tr) return {};
    if (tr.startDate && tr.endDate) return { startDate: tr.startDate, endDate: tr.endDate };
    const toYmd = (d: Date) => d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
    const now = new Date();
    switch (tr.preset) {
      case 'last_week': {
        const d = now.getDay();
        const thisMonday = new Date(now);
        thisMonday.setDate(thisMonday.getDate() + (d === 0 ? -6 : 1 - d));
        const lastSun = new Date(thisMonday);
        lastSun.setDate(lastSun.getDate() - 1);
        const lastMon = new Date(lastSun);
        lastMon.setDate(lastMon.getDate() - 6);
        return { startDate: toYmd(lastMon), endDate: toYmd(lastSun) };
      }
      case 'this_week': {
        const d = now.getDay();
        const mon = new Date(now); mon.setDate(now.getDate() - (d === 0 ? 6 : d - 1));
        const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
        return { startDate: toYmd(mon), endDate: toYmd(sun) };
      }
      case 'week_to_date': {
        const d2 = now.getDay();
        const mon2 = new Date(now); mon2.setDate(now.getDate() - (d2 === 0 ? 6 : d2 - 1));
        return { startDate: toYmd(mon2), endDate: toYmd(now) };
      }
      case 'last_month': {
        const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const end = new Date(now.getFullYear(), now.getMonth(), 0);
        return { startDate: toYmd(start), endDate: toYmd(end) };
      }
      case 'this_month': {
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        return { startDate: toYmd(start), endDate: toYmd(end) };
      }
      case 'month_to_date': {
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        return { startDate: toYmd(start), endDate: toYmd(now) };
      }
      case 'last_quarter': {
        const q = Math.floor(now.getMonth() / 3);
        const start = new Date(now.getFullYear(), ((q - 1 + 4) % 4) * 3, 1);
        const end = new Date(now.getFullYear(), ((q - 1 + 4) % 4) * 3 + 3, 0);
        return { startDate: toYmd(start), endDate: toYmd(end) };
      }
      case 'this_quarter': {
        const q = Math.floor(now.getMonth() / 3);
        const start = new Date(now.getFullYear(), q * 3, 1);
        const end = new Date(now.getFullYear(), q * 3 + 3, 0);
        return { startDate: toYmd(start), endDate: toYmd(end) };
      }
      case 'quarter_to_date': {
        const q2 = Math.floor(now.getMonth() / 3);
        const start = new Date(now.getFullYear(), q2 * 3, 1);
        return { startDate: toYmd(start), endDate: toYmd(now) };
      }
      case 'last_year': {
        const start = new Date(now.getFullYear() - 1, 0, 1);
        const end = new Date(now.getFullYear() - 1, 11, 31);
        return { startDate: toYmd(start), endDate: toYmd(end) };
      }
      case 'this_year': {
        const start = new Date(now.getFullYear(), 0, 1);
        const end = new Date(now.getFullYear(), 11, 31);
        return { startDate: toYmd(start), endDate: toYmd(end) };
      }
      case 'year_to_date': {
        const start = new Date(now.getFullYear(), 0, 1);
        return { startDate: toYmd(start), endDate: toYmd(now) };
      }
      case 'last_7_days': case 'last_14_days': case 'last_30_days': case 'last_60_days': case 'last_90_days': {
        const n = parseInt(tr.preset!.replace('last_', '').replace('_days', ''), 10) || 7;
        const end = new Date(now);
        const start = new Date(now); start.setDate(start.getDate() - n + 1);
        return { startDate: toYmd(start), endDate: toYmd(end) };
      }
      case 'last_12_months': {
        const end = new Date(now);
        const start = new Date(now); start.setMonth(start.getMonth() - 12);
        return { startDate: toYmd(start), endDate: toYmd(end) };
      }
      default: return {};
    }
  };

  // Debounce clockify filter changes to prevent rapid-fire API calls
  const [debouncedClockifyTrigger, setDebouncedClockifyTrigger] = useState(0);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedClockifyTrigger(n => n + 1), 300);
    return () => clearTimeout(timer);
  }, [clockifyActiveView, clockifyCustomFilters, myTimeActiveView, myTimeCustomFilters]);

  // Load Clockify projects with time when view/filters change (debounced)
  useEffect(() => {
    if (selectedDepartment !== 'Time Reports' && selectedDepartment !== 'My Time') return;
    setClockifyError(null);
    setClockifyLoading(true);
    // For "My Time" view, always lock to the current user
    const myTimeUserIds = selectedDepartment === 'My Time' && auth.user?.id
      ? [String(auth.user.id)] : undefined;
    const userIds = myTimeUserIds || clockifyEffectiveFilters.userIds;
    const projectIdsExcluded = clockifyEffectiveFilters.projectIdsExcluded;
    const timeRange = getClockifyTimeRange(clockifyEffectiveFilters.timeRange);
    const hasTimeFilter = !!(timeRange.startDate && timeRange.endDate);
    const hasFilters = (userIds?.length ?? 0) > 0 || (projectIdsExcluded?.length ?? 0) > 0 || hasTimeFilter;
    const params = hasFilters
      ? {
          userIds: userIds?.length ? userIds : undefined,
          projectIdsExcluded: projectIdsExcluded?.length ? projectIdsExcluded : undefined,
          startDate: hasTimeFilter ? timeRange.startDate : undefined,
          endDate: hasTimeFilter ? timeRange.endDate : undefined,
        }
      : undefined;
    const laborParams = params ?? {
      startDate: '2010-01-01',
      endDate: new Date().toISOString().slice(0, 10),
      userIds: userIds?.length ? userIds : undefined,
      projectIdsExcluded: projectIdsExcluded?.length ? projectIdsExcluded : undefined,
    };
    // Compute period days for proration
    if (hasTimeFilter && timeRange.startDate && timeRange.endDate) {
      const s = new Date(timeRange.startDate + 'T00:00:00');
      const e = new Date(timeRange.endDate + 'T00:00:00');
      const days = Math.round((e.getTime() - s.getTime()) / 86400000) + 1;
      setClockifyPeriodDays(days > 0 ? days : null);
    } else {
      setClockifyPeriodDays(null);
    }
    const dateParamsForHours: { startDate?: string; endDate?: string } = {};
    if (hasTimeFilter && timeRange.startDate) dateParamsForHours.startDate = timeRange.startDate;
    if (hasTimeFilter && timeRange.endDate) dateParamsForHours.endDate = timeRange.endDate;
    Promise.all([
      timeReportsApi.getProjects({
        source: reportDataSource,
        userIds: params?.userIds,
        excludeProjects: params?.projectIdsExcluded,
        startDate: params?.startDate,
        endDate: params?.endDate,
      }),
      timeReportsApi.getSettings(),
      timeReportsApi.getLaborCosts({
        source: reportDataSource,
        userIds: laborParams?.userIds,
        excludeProjects: laborParams?.projectIdsExcluded,
        startDate: laborParams?.startDate,
        endDate: laborParams?.endDate,
      }),
      timeReportsApi.getMRR(),
      timeReportsApi.getBudgets(),
      timeReportsApi.getUserHours({
        source: reportDataSource,
        startDate: dateParamsForHours?.startDate,
        endDate: dateParamsForHours?.endDate,
      }),
      aptlyApi.getPortfolioClients(),
    ])
      .then(([projectsRes, settingsRes, laborRes, mrrRes, budgetsRes, userHoursRes, portfolioRes]) => {
        if (projectsRes.success && projectsRes.data) setClockifyProjectsWithTime(Array.isArray(projectsRes.data) ? projectsRes.data : []);
        else setClockifyError('Failed to load');
        if (settingsRes.success && settingsRes.data) {
          const s = settingsRes.data.settings || settingsRes.data;
          setClockifySettings({
            pph_on_call: s.pph_on_call,
            pph_off_call: s.pph_off_call,
            operating_costs: s.operating_costs,
            active_clients: s.active_clients,
            team_capacity_hours: s.team_capacity_hours,
          });
        } else setClockifySettings(null);
        const laborMap = new Map<string, { laborCostOnCall: number; laborCostOffCall: number }>();
        if (laborRes.success && Array.isArray(laborRes.data)) {
          laborRes.data.forEach((r: { projectId: string; laborCostOnCall: number; laborCostOffCall: number }) => {
            laborMap.set(r.projectId, { laborCostOnCall: r.laborCostOnCall, laborCostOffCall: r.laborCostOffCall });
          });
        }
        setClockifyLaborCosts(laborMap);
        const mrrMap = new Map<string, number>();
        if (mrrRes.success && Array.isArray(mrrRes.data)) {
          mrrRes.data.forEach((r: { project_id: string; mrr: number | null }) => {
            if (r.mrr != null) mrrMap.set(r.project_id, r.mrr);
          });
        }
        setClockifyProjectMRRs(mrrMap);
        const budgetMap = new Map<string, { budgeted_on_call: number | null; budgeted_off_call: number | null }>();
        if (budgetsRes.success && Array.isArray(budgetsRes.data)) {
          budgetsRes.data.forEach((b: { project_id: string; budgeted_on_call: number | null; budgeted_off_call: number | null }) => {
            budgetMap.set(b.project_id, { budgeted_on_call: b.budgeted_on_call, budgeted_off_call: b.budgeted_off_call });
          });
        }
        setClockifyProjectBudgets(budgetMap);
        if (userHoursRes.success && Array.isArray(userHoursRes.data)) {
          setClockifyUserProjectHours(userHoursRes.data.map((e: any) => ({
            userId: e.userId, userName: e.userName, projectId: e.projectId, projectName: e.projectName,
            onCallHours: e.onCallHours ?? 0, offCallHours: e.offCallHours ?? 0, totalHours: e.totalHours ?? 0,
            implementationHours: e.implementationHours ?? 0, supportHours: e.supportHours ?? 0,
            documentingHours: e.documentingHours ?? 0, otherHours: e.otherHours ?? 0,
          })));
        } else {
          setClockifyUserProjectHours([]);
        }
        // Portfolio clients  -  map by normalized title (lowercase, stripped non-alphanumeric) for robust cross-reference
        const portfolioMap = new Map<string, { project_manager: string | null; implementer: string | null; expected_wrap_up: string | null; stage: string | null; card_id: string }>();
        if (portfolioRes.success && portfolioRes.data?.clients) {
          for (const c of portfolioRes.data.clients) {
            portfolioMap.set(c.title.toLowerCase().replace(/[^a-z0-9]/g, ''), {
              project_manager: c.project_manager,
              implementer: c.implementer,
              expected_wrap_up: c.expected_wrap_up,
              stage: c.stage,
              card_id: c.card_id,
            });
          }
        }
        setClockifyPortfolioClients(portfolioMap);
      })
      .catch((err) => {
        setClockifyError(err.message || 'Failed to load time reports data');
        setClockifyProjectsWithTime([]);
      })
      .finally(() => setClockifyLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDepartment, refreshSignal, clockifyDataRefreshTrigger, debouncedClockifyTrigger, reportDataSource]);





  const handleAddValue = (metric: ScorecardMetric, weekStart?: string) => {
    if (!auth.hasAction('edit_metric_values')) return;
    setSelectedMetric({
      id: metric.metric_id,
      name: metric.metric_name,
      description: metric.description,
      unit: metric.unit,
      department_id: null,
      metric_type: metric.metric_type || 'kpi',
      source_type: metric.source_type || 'manual',
      created_at: '',
    });
    setSelectedWeek(weekStart || null);
    const weekData = weekStart ? metric.weeks.find((w) => w.week_start === weekStart) : undefined;
    setSelectedWeekCurrentValue(weekData?.weekly_actual ?? undefined);
    setAddValueDialogOpen(true);
  };

  const handleMrrSave = (projectId: string, projectName: string, value: string) => {
    const mrr = value.trim() === '' ? null : parseFloat(value.replace(/[,$]/g, ''));
    if (mrr !== null && (isNaN(mrr) || mrr < 0)) return;
    timeReportsApi.setMRR(projectId, projectName, mrr ?? 0).then((res) => {
      if (res.success) {
        const newMap = new Map(clockifyProjectMRRs);
        if (mrr != null) newMap.set(projectId, mrr);
        else newMap.delete(projectId);
        setClockifyProjectMRRs(newMap);
      }
    });
    setEditingMrrProjectId(null);
  };

  const handleBudgetSave = (projectId: string, projectName: string, field: 'on' | 'off', value: string) => {
    const parsed = value.trim() === '' ? null : parseFloat(value);
    if (parsed !== null && (isNaN(parsed) || parsed < 0)) return;
    const existing = clockifyProjectBudgets.get(projectId) ?? { budgeted_on_call: null, budgeted_off_call: null };
    const updated = {
      project_id: projectId,
      project_name: projectName,
      budgeted_on_call: field === 'on' ? parsed : existing.budgeted_on_call,
      budgeted_off_call: field === 'off' ? parsed : existing.budgeted_off_call,
    };
    timeReportsApi.setBudget(projectId, projectName, updated.budgeted_on_call ?? 0, updated.budgeted_off_call ?? 0).then((res) => {
      if (res.success) {
        const newMap = new Map(clockifyProjectBudgets);
        newMap.set(projectId, { budgeted_on_call: updated.budgeted_on_call, budgeted_off_call: updated.budgeted_off_call });
        setClockifyProjectBudgets(newMap);
      }
    });
    setEditingBudgetProjectId(null);
    setEditingBudgetField(null);
  };

  const _handleQuickAdjust = async (metric: ScorecardMetric, weekStart: string, delta: number) => {
    if (!weekStart) return;
    if (isApiMetric(metric)) return;
    try {
      setAdjustingCell(`${metric.metric_id}-${weekStart}-${delta}`);
      const week = metric.weeks.find((w) => w.week_start === weekStart);
      const currentValue = week?.weekly_actual || 0;
      const nextValue = currentValue + delta;
      await metricsApi.addValue(metric.metric_id, nextValue, weekStart);
      await loadScorecard();
    } catch (error) {
      console.error('Failed to adjust metric value:', error);
    } finally {
      setAdjustingCell(null);
    }
  };

  const handleValueAdded = async () => {
    try {
      await loadScorecard();
    } catch (err) {
      console.error('Error refreshing scorecard:', err);
      // Error is already handled in loadScorecard
    }
  };

  const getQuarterLabelForDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const month = date.getMonth(); // 0-11
    const quarter = Math.floor(month / 3) + 1;
    const year = date.getFullYear();
    return `Q${quarter} ${year}`;
  };

  const _getQuarterRangeLabel = () => {
    if (dateFilterStart || dateFilterEnd) {
      if (dateFilterStart && dateFilterEnd) {
        const startLabel = getQuarterLabelForDate(dateFilterStart);
        const endLabel = getQuarterLabelForDate(dateFilterEnd);
        if (startLabel === endLabel) {
          return startLabel;
        }
        return `${startLabel}  -  ${endLabel}`;
      }
      return 'Custom Range';
    }

    if (quarterInfo) {
      return `Q${quarterInfo.quarter} ${quarterInfo.year}`;
    }

    return 'Quarter View';
  };

  const _handleDeleteMetric = (metricId: number, metricName: string) => {
    if (!auth.hasAction('manage_metrics')) return;
    showConfirm({
      message: `Are you sure you want to remove the metric "${metricName}"? You can undo this from the message that appears.`,
      variant: 'danger',
      confirmLabel: 'Remove',
      onConfirm: async () => {
        try {
          await metricsApi.delete(metricId);
          setUndoMetricId(metricId);
          setUndoMetricName(metricName);
          setUndoSnackbarOpen(true);
          await loadScorecard(); // Refresh the scorecard
        } catch (error: any) {
          console.error('Error deleting metric:', error);
          alert(`Failed to delete metric: ${extractApiError(error)}`);
        }
      },
    });
  };

  const handleUndoDelete = async () => {
    if (undoMetricId == null) return;
    setUndoSnackbarOpen(false);
    try {
      await metricsApi.restore(undoMetricId);
      setUndoMetricId(null);
      setUndoMetricName('');
      await loadScorecard();
    } catch (error: any) {
      console.error('Error restoring metric:', error);
      alert(`Failed to restore: ${extractApiError(error)}`);
      setUndoMetricId(null);
      setUndoMetricName('');
    }
  };

  const handleCloseUndoSnackbar = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setUndoSnackbarOpen(false);
    setUndoMetricId(null);
    setUndoMetricName('');
  };

  const normalizeDepartment = (value: string) => value.trim().toLowerCase();
  const filteredScorecard = useMemo(() => scorecard.filter((metric) => {
    if (showScorecardsChrome) {
      const f = scorecardEffectiveFilters;
      if (f.departments && f.departments.length > 0) {
        if (!metric.department_name || !f.departments.some((d) => normalizeDepartment(d) === normalizeDepartment(metric.department_name!))) {
          return false;
        }
      }
      if (f.metricIds && f.metricIds.length > 0) {
        if (!f.metricIds.includes(metric.metric_id)) return false;
      }
      return true;
    }
    if (selectedDepartment === 'My Scorecard') {
      const dept = auth.impersonation.active ? auth.impersonation.department : 'Operations';
      if (!dept) return true;
      return metric.department_name ? normalizeDepartment(metric.department_name) === normalizeDepartment(dept) : false;
    }
    if (!metric.department_name) {
      return false;
    }
    return normalizeDepartment(metric.department_name) === normalizeDepartment(selectedDepartment);
  }), [scorecard, selectedDepartment, showScorecardsChrome, scorecardEffectiveFilters, auth.impersonation.active, auth.impersonation.department]);

  const reportsPrimaryTitle = useMemo(() => {
    if (selectedDepartment !== 'Reports' || !reportSubPath || reportSubPath === 'scorecards') return null;
    const map: Record<string, string> = {
      insights: 'Insights',
      capacity: 'Capacity',
      inbox: 'Inbox reports',
    };
    return map[reportSubPath] ?? reportSubPath;
  }, [selectedDepartment, reportSubPath]);

  useEffect(() => {
    if (filteredScorecard.length === 0) return;
    const metricIds = Array.from(new Set(filteredScorecard.map(m => m.metric_id)));
    Promise.all(metricIds.map(id => metricsApi.getAnnotations(id)))
      .then(results => {
        const byMetric: Record<number, Array<{ id: number; weekStart: string; note: string }>> = {};
        metricIds.forEach((id, i) => {
          byMetric[id] = (results[i] || []).map((a: { id: number; weekStart: string; note: string }) => ({ id: a.id, weekStart: a.weekStart, note: a.note }));
        });
        setAnnotationsByMetric(byMetric);
      })
      .catch(() => { /* ignore */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredScorecard.map(m => m.metric_id).join(',')]);

  // Weeks for table columns (after hooks so hooks run unconditionally)
  const weeks: string[] = quarterInfo
    ? quarterInfo.sundays.slice(0, 13)
    : scorecard.length > 0 && scorecard[0].weeks.length > 0
      ? scorecard[0].weeks.map(w => w.week_start)
      : [];

  const needsScorecard =
    canReadScorecard &&
    ((selectedDepartment === 'Reports' && reportSubPath === 'scorecards') ||
      (!NON_SCORECARD_DEPARTMENTS.has(selectedDepartment) && selectedDepartment !== 'Reports'));

  // Entry 542 (REVIEW-2): the three guards below replace the WHOLE shell, rail
  // included, with a spinner, an error or "No data available". They are for the
  // pages that draw the scorecard (Scorecards, My Scorecard, a department's own
  // scorecard page). The pages with their own view in the dispatch below never
  // read it, yet sat behind that full-screen spinner on every visit while the
  // scorecard reloaded, so the rail "went away" on Calendar, Projects, To-dos,
  // Clients and Help Desk (Michael's Loom, 2026-10-29). They now draw at once;
  // the scorecard still loads in the background exactly as before. (My Week
  // reads it but already draws its empty case.)
  const OWN_VIEW_DEPARTMENTS = new Set([
    'My Week', 'Analytics', 'Operations', 'Engineering', 'Contacts', 'Clients',
    'Templates', 'Scheduling', 'Forms', 'Documentation Nation', 'SmokeTests',
    'AiMissionControl', 'HelpDesk', 'PersonalInbox', 'Todos', 'Comms',
  ]);
  const scorecardOnScreen = needsScorecard
    && (showScorecardsChrome || selectedDepartment === 'My Scorecard' || !OWN_VIEW_DEPARTMENTS.has(selectedDepartment));

  if (scorecardOnScreen && loading) {
    return (
      <Box p={3} display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (scorecardOnScreen && error) {
    return (
      <Box p={3}>
        <Typography color="error" variant="h6" gutterBottom>
          Error Loading Scorecard
        </Typography>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (scorecardOnScreen && weeks.length === 0) {
    return <Typography>No data available</Typography>;
  }

  const _renderPaceCell = (metric: ScorecardMetric) => {
    const weekKey = getCurrentWeekKey(weeks);
    const week = metric.weeks.find((w) => w.week_start === weekKey);
    if (!isCurrentQuarterView() || !week || metric.quarter_target === null) {
      return <Typography component="div" variant="body2">N/A</Typography>;
    }
    if (metric.target_type === 'threshold') {
      const delta = week.average_actual - week.weekly_target;
      let color = 'warning.main';
      if (metric.threshold_direction === 'below') {
        if (delta < 0) color = 'success.main';
        if (delta > 0) color = 'error.main';
      } else {
        if (delta > 0) color = 'success.main';
        if (delta < 0) color = 'error.main';
      }
      const formatted = formatValue(Math.abs(delta), null);
      const display = delta > 0 ? `+${formatted}` : (delta < 0 ? `-${formatted}` : '0');
      return (
        <Typography component="div" variant="body2" fontWeight="bold" sx={{ color }}>
          {display}
        </Typography>
      );
    }
    const delta =
      metric.evaluation_mode === 'rolling'
        ? week.average_actual - week.average_target
        : week.cumulative_actual - week.cumulative_target;
    let color = 'warning.main';
    if (delta > 0) color = 'success.main';
    if (delta < 0) color = 'error.main';
    const formatted = formatValue(Math.abs(delta), null);
    const display = delta > 0 ? `+${formatted}` : (delta < 0 ? `-${formatted}` : '0');
    return (
      <Typography component="div" variant="body2" fontWeight="bold" sx={{ color }}>
        {display}
      </Typography>
    );
  };

  // Metric menu handlers moved to Settings page

  const sidebarNavItemSx = (isSelected: boolean) => ({
    borderRadius: 1.5,
    mb: 0.25,
    py: 0.75,
    px: departmentsOpen ? 1.5 : 1,
    justifyContent: departmentsOpen ? 'initial' : 'center',
    '&.Mui-selected': {
      bgcolor: (t: any) => alpha(t.palette.primary.main, 0.12),
      '&:hover': { bgcolor: (t: any) => alpha(t.palette.primary.main, 0.18) },
    },
  });

  const renderNavItem = (department: string, label: string, badgeCount?: number) => {
    const isSelected = selectedDepartment === department ||
      (department === 'HelpDesk' && selectedDepartment === 'Inbox');
    const icon = departmentIconMap[department] || departmentIconMap[label];
    return (
      <Tooltip key={department} title={departmentsOpen ? '' : label} placement="right">
        <ListItemButton
          className="bsg-sidebar-nav-item"
          selected={isSelected}
          component="a"
          href={departmentToPath(department)}
          draggable={false}
          onClick={(e: React.MouseEvent) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
            e.preventDefault();
            handleSelectDepartment(department);
          }}
          sx={sidebarNavItemSx(isSelected)}
        >
          <ListItemIcon sx={{ minWidth: departmentsOpen ? 36 : 'auto', color: isSelected ? 'primary.main' : 'text.secondary' }}>
            {badgeCount ? (
              <Badge badgeContent={badgeCount} max={99} sx={{ '& .MuiBadge-badge': { fontSize: '10.5px', height: 15, minWidth: 15 } }}>
                {icon}
              </Badge>
            ) : icon}
          </ListItemIcon>
          {departmentsOpen && (
            <ListItemText
              primary={label}
              primaryTypographyProps={{ fontSize: '13.5px', fontWeight: isSelected ? 600 : 400 }}
            />
          )}
        </ListItemButton>
      </Tooltip>
    );
  };

  // rail v2 (navCtx / handleNavClick are defined above the
  // scorecard early-returns to satisfy rules-of-hooks). activeNavKey is a plain
  // derived value (not a hook), so it lives here next to its presenter.
  const activeNavKey = navItemForPath(navCtx, location.pathname)?.key;

  // Presenter for a resolved nav item. Navigates by explicit path (selection is
  // a path-match, so sub-path items like /operations/projects highlight too).
  // `dragHandleProps` (Phase 5) is passed in the DnD render path → a hover grip.
  return (
    /* state.md Entry 308 — `height:'100%'`, NOT `minHeight:'100vh'`. This Box
       does not start at the top of the document: it sits below the app bar and
       inside the Container's top margin, so demanding a full viewport of height
       made every page exactly 105px taller than the window (the app-wide dead
       scroll, worst on the flush Email / Help Desk surfaces). `100%` is the
       scroll port's height, and being DEFINITE it lets the content column
       below hand a real height to whatever view is dispatched — which is what
       retires `calc(100vh - N)` guessing inside the shell. Content taller than
       the port still overflows normally and the Container scrolls it. */
    <Box sx={{ display: 'flex', gap: 0, height: '100%', minHeight: 0, bgcolor: 'var(--bg)' }}>
      <ConfirmDialog {...dialogProps} />
      {/* CD-12 / 4A (state.md Entry 254): the company rail is HubRail. The
          legacy MUI <Paper> rail is retained ONLY for isPersonalView (team
          members with no company tab keep My Week / My Scorecard / My Tickets /
          My Time, which HubRail doesn't carry). The company branch inside this
          Paper is now unreachable — a follow-up removes it. */}
      {isPersonalView ? (
      <Paper
        elevation={0}
        sx={{
          width: departmentsOpen ? 264 : 72,
          transition: 'width 0.2s ease',
          flexShrink: 0,
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          position: 'fixed',
          top: 0,
          left: 0,
          zIndex: 1201,
          overflow: 'hidden',
          borderRight: '1px solid',
          borderColor: 'divider',
          borderRadius: 0,
        }}
      >
        {/* Branding header */}
        <Box sx={{
          display: 'flex', alignItems: 'center',
          justifyContent: departmentsOpen ? 'space-between' : 'center',
          px: departmentsOpen ? 1.5 : 0, py: 1, minHeight: 56,
        }}>
          {departmentsOpen ? (
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
              onClick={() => navigate(departmentToPath('Dashboard'))}
            >
              <img
                src={process.env.PUBLIC_URL + (sidebarIsDark ? '/logo-icon-white.png' : '/logo-icon-black.png')}
                alt="The Process Lounge"
                style={{ height: 36, objectFit: 'contain' }}
              />
              <Typography className="bsg-logo" variant="subtitle2" fontWeight={700} noWrap sx={{ color: 'primary.main' }}>
                The Process Lounge
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{ cursor: 'pointer', display: 'flex', justifyContent: 'center' }}
              onClick={() => navigate(departmentToPath('Dashboard'))}
            >
              <img
                src={process.env.PUBLIC_URL + (sidebarIsDark ? '/logo-icon-white.png' : '/logo-icon-black.png')}
                alt="The Process Lounge"
                style={{ height: 28, objectFit: 'contain' }}
              />
            </Box>
          )}
          <IconButton onClick={() => setDepartmentsOpen(prev => !prev)} size="small">
            {departmentsOpen ? <ChevronLeftIcon fontSize="small" /> : <MenuIcon fontSize="small" />}
          </IconButton>
        </Box>
        <Divider />

        <List dense sx={{ px: departmentsOpen ? 1 : 0.5, pt: 1, flexGrow: 1, overflow: 'auto' }}>
          {/* Perf 3g (Entry 352): this was a ternary whose else branch held the
              full drag-to-reorder rail (DragDropContext + renderNavItemV2 /
              renderFavoriteRow / …). The <Paper> only renders when
              isPersonalView is TRUE, so that branch needed the flag true and
              false at once — provably unreachable since CD-12/4A moved the
              company rail to HubRail (Entry 254: "a follow-up removes it").
              This is that follow-up; the personal rail is byte-identical. */}
          {departmentOptions.map((dept) => renderNavItem(dept, dept, dept === 'My Tickets' ? myTicketsCount : undefined))}
        </List>

        {/* The What's New / Submit-a-Request footer sat behind !isPersonalView
            — unreachable inside this personal-only Paper (Entry 352). */}
      </Paper>
      ) : (
        <HubRail
          navCtx={navCtx}
          activePath={location.pathname}
          onNavigate={(p) => { navigate(p); setSelectedDepartment(parsePathname(p.split(/[?#]/)[0]).department); }}
          collapsed={!departmentsOpen}
          onToggleCollapse={() => setDepartmentsOpen((v) => !v)}
          badges={{ inbox: inboxUnreadCount, helpdesk: helpDeskUnreadCount, forms: formsUnreadCount, whatsnew: whatsNewUnread }}
          user={{
            initials: ((auth.user?.displayName ?? auth.user?.email ?? 'U').split(/[\s@.]+/).map((s: string) => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || 'U'),
            name: auth.user?.displayName ?? auth.user?.email ?? '',
            role: auth.user?.roleName ?? '',
          }}
          onLogout={() => { void auth.logout(); }}
          /* FR7/FR8: the rail's What's New + Submit a Request rows are dialogs,
             not routes — wire them to the dialogs already mounted below. The
             old `/whats-new` route never existed (fell through to Dashboard),
             and Submit a Request had no live rail entry after the nav rebuild. */
          onWhatsNew={() => setWhatsNewOpen(true)}
          onFeatureRequest={() => setFeatureRequestOpen(true)}
        />
      )}

      {/* I5.2 (state.md Entry 9): module-render wrapper. The `flush`
          opt-in suppresses wrapper padding AND the page-title row for
          inbox-family modules that carry their own header chrome. The
          flush predicate is owned by `./inbox/flushModules.ts` so the
          "I have my own header chrome" knowledge belongs to the child
          modules — AppShell only consults the predicate. */}
      {/* state.md Entry 308 — the content column is a FLEX COLUMN with
          `minHeight:0`. The header band below is an auto-height row, so a view
          that wants "the rest of the height" says `flex:1; minHeight:0` and
          gets exactly what is left AFTER the band — no arithmetic, and it stays
          correct when the band wraps to two lines or an impersonation/dev
          banner appears. That is what replaced the ~17 hand-guessed
          `calc(100vh - N)` offsets; see CLAUDE.md "THE HEIGHT GATE". Views that
          size to their content keep the default `flex:0 1 auto` and stack as
          they did in block flow. */}
      <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', pb: 1.5, ...(isFlushDepartment(selectedDepartment, reportSubPath) ? {} : { pl: 1.5, pr: 1, pt: 0.5 }) }}>
        {!isFlushDepartment(selectedDepartment, reportSubPath) && (
        <Box sx={{ flexShrink: 0, mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
              {reportsPrimaryTitle
                ? reportsPrimaryTitle
                : showScorecardsChrome
              ? `Scorecards . Q${quarter ?? 1} ${year ?? new Date().getFullYear()}`
              : selectedDepartment === 'Dashboard'
              ? 'Center Console'
              : selectedDepartment === 'My Scorecard'
              ? `My Scorecard . Q${quarter ?? 1} ${year ?? new Date().getFullYear()}`
              : selectedDepartment === 'My Tickets'
              ? 'My Tickets'
              : selectedDepartment === 'My Time'
              ? ''
              : selectedDepartment === 'Time Reports'
              ? ''
              : selectedDepartment === 'Time Tracking'
              ? ''
              : selectedDepartment === 'Phones'
              ? ''
              : selectedDepartment === 'Insights'
              ? 'Insights'
              : selectedDepartment === 'Sales'
              ? ''
              : selectedDepartment === 'EOS'
              ? ''
              : selectedDepartment === 'Engineering'
              ? ''
              : selectedDepartment === 'Contacts'
              ? ''
              : selectedDepartment === 'Clients'
              ? ''
              : selectedDepartment === 'Reports'
              ? ''
              : selectedDepartment === 'Templates'
              ? ''
              : selectedDepartment === 'Scheduling'
              ? ''
              : selectedDepartment === 'Forms'
              ? ''
              : selectedDepartment === 'Documentation Nation'
              ? ''
              : selectedDepartment === 'Proposals'
              ? ''
              : selectedDepartment === 'Mental Herd'
              ? ''
              : selectedDepartment === 'Inbox' || selectedDepartment === 'HelpDesk' || selectedDepartment === 'PersonalInbox'
              ? ''
              : selectedDepartment === 'Todos'
              ? ''
              : selectedDepartment === 'Operations'
              ? ''
              : selectedDepartment === 'Finances'
              ? 'Finances'
              : selectedDepartment === 'Settings'
              ? 'Settings'
              : selectedDepartment === 'Call Intelligence'
              ? 'Lord of the Notes'
              // Root-cause purge (state.md Entry 190): the fallback used to
              // append "· Q<quarter> <year>" to ANY unmapped department, so a
              // brand-new department (e.g. AiMissionControl) inherited a
              // spurious quarter. The quarter is a Scorecards-only concern; the
              // generic fallback is just the department name, no quarter.
              : selectedDepartment}
            </Typography>
            {reportsPrimaryTitle && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Reports
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GlobalQuickAdd />
            {/* -- Inline filter controls (scorecard / department views) -- */}
            {selectedDepartment !== 'Dashboard' && selectedDepartment !== 'Time Reports' && selectedDepartment !== 'Time Tracking' && selectedDepartment !== 'Phones' && selectedDepartment !== 'Finances' && selectedDepartment !== 'Settings' && selectedDepartment !== 'My Tickets' && selectedDepartment !== 'My Time' && selectedDepartment !== 'Insights' && selectedDepartment !== 'Sales' && selectedDepartment !== 'EOS' && selectedDepartment !== 'Engineering' && selectedDepartment !== 'Inbox' && selectedDepartment !== 'HelpDesk' && selectedDepartment !== 'PersonalInbox' && selectedDepartment !== 'Todos' && selectedDepartment !== 'Operations' && selectedDepartment !== 'Contacts' && selectedDepartment !== 'Clients' && selectedDepartment !== 'Mental Herd' && !(selectedDepartment === 'Reports' && reportSubPath !== 'scorecards') && (
              <>
                {/* Person filter (Scorecards) */}
                {showScorecardsChrome && scorecardPeopleList.length > 0 && (
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel id="scorecard-people-title-label" sx={{ fontSize: '12.5px' }}>Person</InputLabel>
                    <Select
                      labelId="scorecard-people-title-label"
                      multiple
                      value={scorecardSelectedPeople}
                      label="Person"
                      onChange={(e: SelectChangeEvent<string[]>) => {
                        const val = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                        setScorecardSelectedPeople(val);
                        if (val.length > 0) {
                          setScorecardActiveView('custom');
                          setScorecardCustomFilters((prev) => ({ ...prev, assignees: val }));
                        } else {
                          setScorecardCustomFilters((prev) => {
                            const { assignees: _, ...rest } = prev;
                            return rest;
                          });
                        }
                      }}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25 }}>
                          {(selected as string[]).map((v) => (
                            <Chip key={v} label={v} size="small" sx={{ height: 20, fontSize: '11px' }} />
                          ))}
                        </Box>
                      )}
                      sx={{ height: 32, fontSize: '12.5px' }}
                    >
                      {scorecardPeopleList.map((name) => (
                        <MenuItem key={name} value={name} sx={{ fontSize: '12.5px' }}>
                          <Checkbox checked={scorecardSelectedPeople.indexOf(name) > -1} size="small" />
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {scorecardSelectedPeople.length > 0 && (
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => {
                      setScorecardSelectedPeople([]);
                      setScorecardCustomFilters((prev) => {
                        const { assignees: _, ...rest } = prev;
                        return rest;
                      });
                    }}
                    sx={{ textTransform: 'none', fontSize: '11px', py: 0, px: 0.5, minWidth: 0, color: 'text.secondary' }}
                  >
                    Clear
                  </Button>
                )}
                {showScorecardsChrome && (
                  <DateRangeFilter
                    onFilterChange={(start, end) => {
                      setDateFilterStart(start);
                      setDateFilterEnd(end);
                    }}
                    currentStartDate={dateFilterStart}
                    currentEndDate={dateFilterEnd}
                  />
                )}
              </>
            )}
            {/* Time Reports filters / views / columns / export / data-source now live inside <TimeReportsView/> (the Tracker/Reports split). */}
            {(showScorecardsChrome || selectedDepartment === 'My Scorecard') && (
            <IconButton
              size="small"
              onClick={(e) => {
                (e.currentTarget as HTMLElement).blur();
                setVisibilityDialogOpen(true);
              }}
              title="Show/Hide Metrics"
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
            )}
            {/* Export CSV and Print PDF buttons. State.md Entry 62 —
                dropped `|| selectedDepartment === 'Operations'` from
                the condition. These buttons only ever export scorecard
                data, so showing them on Operations sub-pages (Tickets,
                Clients, Time Entries, etc.) was misleading + noisy. */}
            {(showScorecardsChrome || selectedDepartment === 'My Scorecard') && filteredScorecard.length > 0 && (
              <>
                <Tooltip title="Export to CSV">
                  <IconButton
                    size="small"
                    onClick={() => {
                      const weeksData = filteredScorecard[0]?.weeks || [];
                      const headers = ['Metric', 'Department', 'Unit', 'Target', ...weeksData.map(w => w.week_start)];
                      const rows = filteredScorecard.map(m => [
                        m.metric_name,
                        m.department_name || '',
                        m.unit || '',
                        m.quarter_target != null ? String(m.quarter_target) : '',
                        ...m.weeks.map(w => String(w.weekly_actual ?? '')),
                      ]);
                      exportToCsv(`scorecard-${selectedDepartment}-${new Date().toISOString().split('T')[0]}.csv`, headers, rows);
                    }}
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Print / Export PDF">
                  <IconButton
                    size="small"
                    onClick={() => {
                      const printWindow = window.open('', '_blank');
                      if (!printWindow) {
                        toast.error('Pop-up blocked. Please allow pop-ups to export PDF.');
                        return;
                      }
                      const html = buildScorecardHtml({
                        scorecard: filteredScorecard,
                        weeks,
                        quarterInfo,
                        departmentName: showScorecardsChrome ? undefined : selectedDepartment,
                      });
                      printWindow.document.write(html);
                      printWindow.document.close();
                      printWindow.print();
                    }}
                  >
                    <PrintIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            
          </Box>
        </Box>
        )}
        {/* -- Personal Dashboard -- */}
        {/* Entry 542: a new version is offered with a Refresh button, never forced. */}
        <NewVersionWatcher />
        {/* Entry 541: the view swaps in place (no 300ms fade from blank on every page change —
            Michael, 2026-10-29: "things should smoothly transition from screen to screen without
            refreshing"). The key still gives each department a fresh mount. Entry 542 (D-057):
            the new page fades in over 120ms from 45%, never from blank; the rail never moves. */}
        <React.Fragment key={selectedDepartment}>
          {/* state.md Entry 308 — this Box holds the ENTIRE department dispatch,
              so it is the last link in the height chain: content column → HERE →
              the dispatched view. It was `width:100%` (display:block), which
              severed the chain — a view asking for `flex:1` had a block parent,
              so it collapsed to content height (the Help Desk regression caught
              in self-smoke: the inbox rendered 477px tall inside a 1049px
              column). It now carries the column's flex-column contract down one
              more level. Views that size to their content are unaffected: they
              keep the default `flex:0 1 auto` and `min-height:auto`, so they lay
              out exactly as they did in block flow. */}
          <Box className="ds-page-in" sx={{ width: '100%', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {selectedDepartment === 'Dashboard' ? (
          <Suspense fallback={<LoadingOverlay />}>
            <PersonalDashboard />
          </Suspense>
        ) : selectedDepartment === 'My Week' ? (
          <Box sx={{ width: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '20px' }}>
              My Week
            </Typography>

            {/* ── My Day Summary Bar ── */}
            {(() => {
              const CAPACITY = 7;
              const TASK_COLORS: Record<string, string> = {
                'On Call': '#42A5F5', 'Implementing': '#AB47BC', 'Documenting': '#26A69A',
                'Support': '#FFA726', 'Rocks': '#C9A961', 'Misc Todos': '#78909C',
                'Internal Implementation': '#5C6BC0',
              };
              const totalSec = myDaySummary.reduce((s, p) => s + p.total_seconds, 0);
              const totalH = totalSec / 3600;
              const remaining = Math.max(0, CAPACITY - totalH);
              const over = totalH > CAPACITY;

              const taskMap = new Map<string, number>();
              myDaySummary.forEach(p => p.tasks.forEach(t => {
                taskMap.set(t.task_name, (taskMap.get(t.task_name) || 0) + t.total_seconds);
              }));
              const segments = Array.from(taskMap.entries())
                .filter(([, s]) => s > 0)
                .map(([name, secs]) => ({
                  name, hours: secs / 3600, color: TASK_COLORS[name] || '#90A4AE',
                  pct: CAPACITY * 3600 > 0 ? (secs / (CAPACITY * 3600)) * 100 : 0,
                }))
                .sort((a, b) => b.hours - a.hours);

              const fmtH = (sec: number) => {
                const h = Math.floor(sec / 3600); const m = Math.floor((sec % 3600) / 60); const s2 = sec % 60;
                if (h === 0 && m === 0) return `${s2}s`;
                if (h === 0) return `${m}m ${s2}s`;
                return `${h}h ${m}m ${s2}s`;
              };

              return (
                <Paper elevation={0} sx={{ mb: 2, p: 1.5, borderRadius: '6px', border: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.25, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <TodayIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      <Typography variant="subtitle2" sx={{ fontSize: '0.78rem', fontWeight: 700 }}>My Day</Typography>
                    </Box>
                    <Chip label={`${fmtH(totalSec)} tracked`} size="small" variant="outlined"
                      sx={{ fontWeight: 700, fontSize: '11px', borderColor: totalH > 0 ? 'success.main' : 'divider', color: totalH > 0 ? 'success.main' : 'text.secondary' }} />
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '11px' }}>
                      {fmtH(totalSec)} / {CAPACITY}h
                      {!over && ` (${fmtH(Math.round(remaining * 3600))} remaining)`}
                      {over && ' (over capacity)'}
                    </Typography>
                  </Box>
                  <Tooltip title={segments.map(s => `${s.name}: ${s.hours.toFixed(1)}h`).join(' \u00b7 ') || 'No time tracked'} placement="top">
                    <Box sx={{
                      display: 'flex', height: 16, borderRadius: 1, overflow: 'hidden',
                      bgcolor: (t) => alpha(t.palette.text.primary, 0.08),
                      border: over ? '1.5px solid' : 'none', borderColor: 'error.main',
                    }}>
                      {segments.map(s => (
                        <Box key={s.name} sx={{ width: `${Math.min(s.pct, 100)}%`, bgcolor: s.color, transition: 'width 0.3s', minWidth: s.pct > 0 ? 2 : 0 }} />
                      ))}
                    </Box>
                  </Tooltip>
                  {segments.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 1.5, mt: 0.75, flexWrap: 'wrap' }}>
                      {segments.map(item => (
                        <Box key={item.name} sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: item.color }} />
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10.5px' }}>
                            {item.name} {item.hours.toFixed(1)}h
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Paper>
              );
            })()}

            {/* ── Weekly Timesheet (Collapsible) ── */}
            <Paper elevation={0} sx={{ mb: 3, borderRadius: '6px', border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
              <Box
                onClick={() => setTimesheetExpanded(prev => !prev)}
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  px: 2, py: 1.25, cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TimerIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                  <Typography variant="subtitle2" sx={{ fontSize: '13.5px', fontWeight: 700 }}>This Week&apos;s Timesheet</Typography>
                </Box>
                {timesheetExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
              </Box>
              <Collapse in={timesheetExpanded}>
                <Divider />
                <Box sx={{ p: 0 }}>
                  <TimesheetView dateRange={weekDateRange} userId={auth.user?.id} />
                </Box>
              </Collapse>
            </Paper>

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 2, mb: 3 }}>
              {/* Utilization card */}
              <Paper sx={{ p: 2, borderRadius: '6px', border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>My Utilization</Typography>
                {auth.user?.id ? (() => {
                  const userId = String(auth.user.id);
                  const myHours = clockifyUserProjectHours.filter(e => e.userId === userId);
                  const total = myHours.reduce((s, e) => s + e.totalHours, 0);
                  const capacity = 160;
                  const pct = capacity > 0 ? (total / capacity) * 100 : 0;
                  return (
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: pct > 100 ? 'error.main' : pct > 80 ? 'warning.main' : 'success.main' }}>
                        {total.toFixed(1)}h
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{pct.toFixed(0)}% of {capacity}h monthly capacity</Typography>
                      <LinearProgress variant="determinate" value={Math.min(pct, 100)} color={pct > 100 ? 'error' : pct > 80 ? 'warning' : 'success'} sx={{ mt: 1, height: 8, borderRadius: 1 }} />
                    </Box>
                  );
                })() : (
                  <Typography variant="body2" color="text.disabled">Time tracking not linked</Typography>
                )}
              </Paper>

              {/* Ticket summary card */}
              <Paper sx={{ p: 2, borderRadius: '6px', border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Tickets This Week</Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'baseline' }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}> - </Typography>
                  <Typography variant="body2" color="text.secondary">Check My Tickets for details</Typography>
                </Box>
              </Paper>

              {/* Scorecard status card */}
              <Paper sx={{ p: 2, borderRadius: '6px', border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Scorecard Highlights</Typography>
                {filteredScorecard.length > 0 ? (() => {
                  const onTrack = filteredScorecard.filter(m => {
                    const lastWeek = m.weeks.filter(w => w.weekly_actual != null && w.weekly_actual !== 0).pop();
                    return lastWeek?.is_on_track;
                  }).length;
                  const offTrack = filteredScorecard.length - onTrack;
                  return (
                    <Box sx={{ display: 'flex', gap: 3 }}>
                      <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>{onTrack}</Typography>
                        <Typography variant="caption" color="text.secondary">On track</Typography>
                      </Box>
                      <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: offTrack > 0 ? 'error.main' : 'text.secondary' }}>{offTrack}</Typography>
                        <Typography variant="caption" color="text.secondary">Off track</Typography>
                      </Box>
                    </Box>
                  );
                })() : (
                  <Typography variant="body2" color="text.disabled">No metrics loaded</Typography>
                )}
              </Paper>
            </Box>

            {/* Quick navigation */}
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', fontSize: '11px', letterSpacing: 0.5 }}>
              Quick Access
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              {[
                { label: 'My Scorecard', icon: <PersonIcon />, dept: 'My Scorecard' },
                { label: 'My Tickets', icon: <AssignmentIcon />, dept: 'My Tickets' },
                { label: 'My Time', icon: <TimerIcon />, dept: 'My Time' },
              ].map(item => (
                <Paper
                  key={item.dept}
                  component="a"
                  href={departmentToPath(item.dept)}
                  draggable={false}
                  onClick={(e: React.MouseEvent) => {
                    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
                    e.preventDefault();
                    handleSelectDepartment(item.dept);
                  }}
                  sx={{
                    p: 2, display: 'flex', alignItems: 'center', gap: 1.5,
                    cursor: 'pointer', textDecoration: 'none', color: 'inherit',
                    borderRadius: '6px', border: '1px solid', borderColor: 'divider',
                    transition: 'all 0.15s',
                    '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(201,169,97,0.05)' },
                    minWidth: 180,
                  }}
                >
                  <Box sx={{ color: 'primary.main' }}>{item.icon}</Box>
                  <Typography variant="body2" fontWeight={500}>{item.label}</Typography>
                </Paper>
              ))}
            </Box>
          </Box>
        ) : /* -- Personal Views (Team Member) -- */
        /* Unmapped identity notice for personal views */
        selectedDepartment === 'My Scorecard' && !auth.identities.aptly?.name ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, px: 2, textAlign: 'center' }}>
            <PersonIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>Your account hasn't been linked yet</Typography>
            <Typography variant="body2" color="text.disabled">Ask your admin to map your identity in Settings &gt; Users &amp; Roles so your scorecard data appears here.</Typography>
          </Box>
        ) : selectedDepartment === 'Time Tracking' || selectedDepartment === 'My Time' ? (
          <Suspense fallback={<Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>}>
            <TimeTrackerMinimal />
          </Suspense>
        ) : selectedDepartment === 'My Tickets' ? (
          <MyTicketsView />
        ) : selectedDepartment === 'Analytics' ? (
          /* Analytics hub (Brain Dead To Read S1-c1, state.md Entry 377): the
             one door to Scorecard · Forecasting · Time Reports · Data Center ·
             Finances, each a path segment owned by AnalyticsHubView's parser.
             The old homes redirect here (routeConfig LEGACY_REDIRECTS). */
          <Suspense fallback={<Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>}>
            <AnalyticsHubView />
          </Suspense>
        ) : selectedDepartment === 'Time Reports' ? (
          <Suspense fallback={<Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>}>
            <TimeReportsView />
          </Suspense>
        ) : selectedDepartment === 'Insights' || (selectedDepartment === 'Reports' && reportSubPath === 'insights') ? (
          <WidgetBoard
            dashboardId={activeDashboardId}
            dashboardMeta={activeDashboardId ? (dashboards.find((d) => d.id === activeDashboardId) || null) : null}
            onDashboardCreated={(id) => {
              setActiveDashboardId(id);
              dashboardsApi.list().then(setDashboards);
            }}
          />
        ) : selectedDepartment === 'Call Intelligence' ? (
          <Suspense fallback={<Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>}>
            <CallIntelligenceDashboard />
          </Suspense>
        ) : (selectedDepartment === 'Reports' && reportSubPath === 'capacity') ? (
          <Suspense fallback={<Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>}>
            <CapacityView />
          </Suspense>
        ) : selectedDepartment === 'Finances' ? (
          <Suspense fallback={<Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>}>
            <FinancesModule />
          </Suspense>) : selectedDepartment === 'Operations' ? (
          <Suspense fallback={<Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>}>
            <OperationsView />
          </Suspense>
        ) : selectedDepartment === 'Sales' ? (
          <Suspense fallback={<Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>}>
            <SalesView />
          </Suspense>
        ) : selectedDepartment === 'EOS' ? (
          <Suspense fallback={<Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>}>
            <EOSView />
          </Suspense>
        ) : selectedDepartment === 'Engineering' ? (
          <Suspense fallback={<Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>}>
            <EngineeringView />
          </Suspense>
        ) : selectedDepartment === 'Marketing' ? (
          <Suspense fallback={<Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>}>
            <MarketingView />
          </Suspense>
        ) : selectedDepartment === 'Mental Herd' ? (
          <Suspense fallback={<Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>}>
            <KnowledgeBaseView />
          </Suspense>
        ) : selectedDepartment === 'Contacts' ? (
          <Suspense fallback={<Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>}>
            <ContactsDatabaseView />
          </Suspense>
        ) : selectedDepartment === 'Clients' ? (
          <Suspense fallback={<Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>}>
            <ClientsDatabaseView />
          </Suspense>
        ) : selectedDepartment === 'Reports' && reportSubPath === 'inbox' ? (
          <Suspense fallback={<Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>}>
            <ReportsView />
          </Suspense>
        ) : selectedDepartment === 'Templates' ? (
          <Suspense fallback={<Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>}>
            <TemplateLibraryView />
          </Suspense>
        ) : selectedDepartment === 'Scheduling' ? (
          <Suspense fallback={<Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>}>
            <SchedulingView />
          </Suspense>
        ) : selectedDepartment === 'Forms' ? (
          <Suspense fallback={<Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>}>
            <FormsView />
          </Suspense>
        ) : selectedDepartment === 'Documentation Nation' ? (
          <Suspense fallback={<Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>}>
            <DocumentationNationView />
          </Suspense>
        ) : selectedDepartment === 'Proposals' ? (
          /* feat/proposal-generator (state.md Entry 137): top-level tool;
             ProposalsView owns /proposals sub-routes. */
          <Suspense fallback={<Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>}>
            <ProposalsView />
          </Suspense>
        ) : selectedDepartment === 'SmokeTests' ? (
          /* Smoke Tests (Admin) — in-app QA checklist runner. Nav-gated by
             manage_settings; the API is gated server-side. Dispatch arm is
             unconditional so admin deep-links resolve. (state.md Entry 180) */
          <Suspense fallback={<Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>}>
            <SmokeTestsPage />
          </Suspense>
        ) : selectedDepartment === 'AiMissionControl' ? (
          /* AI Mission Control (department) — global AI usage + the agent tiles.
             Nav-gated by manage_settings; the /api/ai-brain routes are gated
             server-side. Sub-paths owned by the view's parser. (state.md Entry 189) */
          <Suspense fallback={<Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>}>
            <AiMissionControlView />
          </Suspense>
        ) : selectedDepartment === 'Inbox' || selectedDepartment === 'HelpDesk' ? (
          <Suspense fallback={<Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>}>
            <InboxModule mode="helpdesk" />
          </Suspense>
        ) : selectedDepartment === 'PersonalInbox' ? (
          <Suspense fallback={<Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>}>
            <InboxModule mode="personal" />
          </Suspense>
        ) : selectedDepartment === 'Todos' ? (
          /* rail v2: To-Dos promoted to a top-level tool (/todos). Same
             graduated surface OperationsView renders at /operations/todos. */
          <Suspense fallback={<Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>}>
            {/* Old-todos sunset (Phase 0, 2026-08-20, Michael: "sunset todos"):
                the role-name fork is gone — every role gets the graduated
                surface. The old screen keyed on hardcoded role NAMES, so the
                seeded Manager and Marketing roles silently got the 1,009-line
                legacy view and none of the CD-25 upgrades. */}
            <OperationsTodosView />
          </Suspense>
        ) : selectedDepartment === 'Comms' ? (
          <Suspense fallback={<Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>}>
            <CommsView />
          </Suspense>
        ) : selectedDepartment === 'Settings' ? (
          /* ===============================================================
             SETTINGS  -  Global Metrics & Targets Management
             =============================================================== */
          <Suspense fallback={<Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>}>
            <SettingsPage onEditMetric={onEditMetric} onOpenMetricSettings={onOpenMetricSettings} />
          </Suspense>
        ) : (showScorecardsChrome || selectedDepartment === 'My Scorecard') ? (
          /* Scorecard rebuild P3 (state.md Entry 92): the graduated
             ScorecardsView replaces the inline god-view for
             /reports/scorecards + /my-scorecard. The inline JSX in the
             default arm below is unreachable for these routes; its
             extraction stays deferred. */
          <Suspense fallback={<Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>}>
            <ScorecardsView myScorecard={selectedDepartment === 'My Scorecard'} />
          </Suspense>
        ) : (
          <Box sx={{ overflowX: 'auto', width: '100%' }}>
            <Box sx={{ minWidth: 1200 }}>
              {/* -- Scorecard Saved Views Bar -- */}
              {showScorecardsChrome && (
                <Paper variant="outlined" sx={{ mb: 1, px: 1.5, py: 0.75, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                    <Button
                      variant={scorecardActiveView === 'all' ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() => { setScorecardActiveView('all'); setScorecardCustomFilters({}); setScorecardSelectedPeople([]); }}
                      sx={{ textTransform: 'none', fontSize: '12.5px', py: 0.25, px: 1.5, minWidth: 0 }}
                    >
                      All
                    </Button>
                    {/* Department quick-filter chips */}
                    {departments.map((d) => {
                      const isActive = scorecardEffectiveFilters.departments?.length === 1 && scorecardEffectiveFilters.departments[0] === d.name;
                      return (
                        <Button
                          key={d.id}
                          variant={isActive ? 'contained' : 'outlined'}
                          size="small"
                          onClick={() => {
                            setScorecardActiveView('custom');
                            setScorecardCustomFilters({ departments: [d.name] });
                          }}
                          sx={{ textTransform: 'none', fontSize: '12.5px', py: 0.25, px: 1.5, minWidth: 0 }}
                        >
                          {d.name}
                        </Button>
                      );
                    })}
                    {scorecardSavedViews.map((v) => (
                      <Button
                        key={v.id}
                        variant={scorecardActiveView === v.id ? 'contained' : 'outlined'}
                        size="small"
                        onClick={() => {
                          setScorecardActiveView(v.id);
                          if (v.filters) {
                            setScorecardCustomFilters(v.filters);
                            setScorecardSelectedPeople(v.filters.assignees || []);
                          }
                        }}
                        sx={{ textTransform: 'none', fontSize: '12.5px', py: 0.25, px: 1.5, minWidth: 0, fontWeight: scorecardActiveView === v.id ? 700 : 400 }}
                      >
                        {v.name}
                      </Button>
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    {scorecardActiveView === 'custom' && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<SaveIcon sx={{ fontSize: '14px !important' }} />}
                        onClick={() => {
                          setScorecardSaveViewMode('new');
                          setScorecardSaveViewTargetId(null);
                          setScorecardSaveViewName('');
                          setScorecardSaveDialogOpen(true);
                        }}
                        sx={{ textTransform: 'none', fontSize: '11px', py: 0.25 }}
                      >
                        Save view
                      </Button>
                    )}
                    {typeof scorecardActiveView === 'number' && (
                      <>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setScorecardSaveViewMode('update');
                            setScorecardSaveViewTargetId(scorecardActiveView);
                            const v = scorecardSavedViews.find((sv) => sv.id === scorecardActiveView);
                            setScorecardSaveViewName(v?.name || '');
                            setScorecardSaveDialogOpen(true);
                          }}
                          sx={{ textTransform: 'none', fontSize: '11px', py: 0.25 }}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => {
                            showConfirm({
                              message: 'Delete this saved view?',
                              variant: 'danger',
                              confirmLabel: 'Delete',
                              onConfirm: () => {
                                settingsApi.deleteScorecardView(scorecardActiveView).then(() => {
                                  settingsApi.getScorecardViews().then((r) => {
                                    if (r.success && Array.isArray(r.data)) setScorecardSavedViews(r.data);
                                  });
                                  setScorecardActiveView('all');
                                  setScorecardCustomFilters({});
                                });
                              },
                            });
                          }}
                          sx={{ textTransform: 'none', fontSize: '11px', py: 0.25 }}
                        >
                          Delete
                        </Button>
                      </>
                    )}
                  </Box>
                </Paper>
              )}
              {/* Filter bar is now integrated into the title row above */}

              {/* -- Table -- */}
              <TableContainer component={Paper} sx={{ borderRadius: 1, maxHeight: 'calc(100vh - 200px)', overflow: 'auto' }}>
          <Table size="small" stickyHeader sx={{ minWidth: 1200 }}>
          <TableHead>
            <TableRow>
              {/* Column A: Metric Name */}
              <TableCell
                sx={{
                  position: 'sticky',
                  left: 0,
                  backgroundColor: 'var(--header-bg)',
                  zIndex: 12,
                  borderRight: '2px solid',
                  borderColor: 'divider',
                  borderBottom: '2px solid',
                  borderBottomColor: 'primary.main',
                  minWidth: getColumnWidth('metric'),
                  width: getColumnWidth('metric'),
                  maxWidth: getColumnWidth('metric'),
                  overflow: 'hidden',
                  py: 1,
                }}
              >
                <Box sx={{ position: 'relative' }}>
                  <Typography component="div" variant="caption" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.8, color: 'text.secondary', fontSize: '11px' }}>
                    {getColumnWidth('metric') < 120 ? 'M' : 'Metric'}
                  </Typography>
                  <Box sx={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 6, cursor: 'col-resize', zIndex: 20 }} onMouseDown={(e) => startResize('metric', e)} />
                </Box>
              </TableCell>

              {/* Column B: Targets vs Actuals */}
              <TableCell
                align="center"
                sx={{
                  backgroundColor: 'var(--header-bg)',
                  borderRight: '2px solid',
                  borderColor: 'divider',
                  borderBottom: '2px solid',
                  borderBottomColor: 'primary.main',
                  minWidth: getColumnWidth('summary'),
                  width: getColumnWidth('summary'),
                  maxWidth: getColumnWidth('summary'),
                  position: 'relative',
                  py: 1,
                }}
              >
                <Box sx={{ position: 'relative' }}>
                  <Typography component="div" variant="caption" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.8, color: 'text.secondary', fontSize: '11px' }}>
                    {getColumnWidth('summary') < 120 ? 'T vs A' : 'Targets vs Actuals'}
                  </Typography>
                  <Box sx={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 6, cursor: 'col-resize', zIndex: 20 }} onMouseDown={(e) => startResize('summary', e)} />
                </Box>
              </TableCell>

              {/* Column C: Pace */}
              <TableCell
                align="center"
                sx={{
                  backgroundColor: 'var(--header-bg)',
                  borderRight: '2px solid',
                  borderColor: 'divider',
                  borderBottom: '2px solid',
                  borderBottomColor: 'primary.main',
                  minWidth: getColumnWidth('pace'),
                  width: getColumnWidth('pace'),
                  maxWidth: getColumnWidth('pace'),
                  position: 'relative',
                  py: 1,
                }}
              >
                <Box sx={{ position: 'relative' }}>
                  <Typography component="div" variant="caption" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.8, color: 'text.secondary', fontSize: '11px' }}>
                    {getColumnWidth('pace') < 120 ? 'Pace' : 'Pace vs Target'}
                  </Typography>
                  <Box sx={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 6, cursor: 'col-resize', zIndex: 20 }} onMouseDown={(e) => startResize('pace', e)} />
                </Box>
              </TableCell>

              {/* Weekly columns */}
              {weeks.map((sunday, _wi) => {
                const isCurrentWeek = getCurrentWeekKey(weeks) === sunday;
                return (
                  <TableCell
                    key={sunday}
                    align="center"
                    sx={{
                      backgroundColor: isCurrentWeek ? 'rgba(var(--accent-primary-rgb), 0.12)' : 'var(--header-bg)',
                      borderBottom: '2px solid',
                      borderBottomColor: isCurrentWeek ? 'primary.main' : 'primary.main',
                      minWidth: getColumnWidth(sunday),
                      width: getColumnWidth(sunday),
                      maxWidth: getColumnWidth(sunday),
                      position: 'relative',
                      py: 1,
                    }}
                  >
                    <Box sx={{ position: 'relative' }}>
                      <Typography component="div" variant="caption" fontWeight={isCurrentWeek ? 700 : 600} sx={{ fontSize: '11px', color: isCurrentWeek ? 'primary.main' : 'text.secondary' }}>
                        {formatWeekLabelForWidth(sunday, getColumnWidth(sunday))}
                      </Typography>
                      <Box sx={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 6, cursor: 'col-resize', zIndex: 20 }} onMouseDown={(e) => startResize(sunday, e)} />
                    </Box>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredScorecard.length === 0 ? (
              <TableRow>
                <TableCell colSpan={weeks.length + 3} align="center" sx={{ py: 4 }}>
                  <Typography component="div" color="text.secondary">No metrics found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredScorecard.map((metric, metricIndex) => {
                const metricWeeks = weeks.map(sunday =>
                  metric.weeks.find(w => w.week_start === sunday)
                );
                const isManualEntry = !isApiMetric(metric);
                const rowBg = metricIndex % 2 === 0 ? 'transparent' : 'rgba(var(--overlay-rgb),0.02)';
                const stickyBgEven = 'background.paper';
                const stickyBgOdd = 'var(--sticky-bg-alt)';

                if (metric.metric_type === 'indicator') {
                  return (
                    <TableRow key={metric.metric_id} sx={{ '& td': { py: 0.4, borderBottom: '1px solid rgba(var(--overlay-rgb),0.06)' }, backgroundColor: rowBg, '&:hover': { backgroundColor: 'rgba(var(--accent-primary-rgb), 0.06)' } }}>
                      {/* Metric name */}
                      <TableCell
                        sx={{
                          position: 'sticky', left: 0, backgroundColor: metricIndex % 2 === 0 ? stickyBgEven : stickyBgOdd, zIndex: 9,
                          borderRight: '2px solid', borderColor: 'divider',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minHeight: 28 }}>
                          <Tooltip
                            title={
                              <Box sx={{ maxWidth: 260 }}>
                                <Typography variant="caption" fontWeight={600} display="block">{metric.metric_name}</Typography>
                                {metric.description && <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.85 }}>{metric.description}</Typography>}
                                <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.7 }}>{getMetricSourceLabel(metric)}</Typography>
                                {metric.department_name && <Chip label={metric.department_name} size="small" sx={{ mt: 0.5, height: 18, fontSize: '10.5px' }} />}
                              </Box>
                            }
                            placement="right"
                          >
                            <Typography variant="body2" fontWeight={500} noWrap sx={{ flex: 1, cursor: 'default', fontSize: '12.5px' }}>
                              {metric.metric_name}
                            </Typography>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      {/* Summary */}
                      <TableCell align="center" sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
                        <Typography component="div" variant="body2" fontWeight={600} sx={{ fontSize: '12.5px', color: (metric.quarter_actual != null && metric.quarter_actual < 0) ? '#f44336' : undefined }}>
                          {formatValue(metric.quarter_actual, metric.unit)}
                        </Typography>
                        {metric.quarter_target != null && (
                          <Typography component="div" variant="caption" sx={{ fontSize: '10.5px', opacity: 0.6 }}>
                            / {formatValue(metric.quarter_target, metric.unit)}
                          </Typography>
                        )}
                      </TableCell>
                      {/* Pace */}
                      <TableCell align="center" sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
                        <Typography component="div" variant="caption" color="text.secondary"> - </Typography>
                      </TableCell>
                      {/* Weekly cells */}
                      {metricWeeks.map((week, index) => {
                        const sunday = weeks[index];
                        const isCurrentWeek = getCurrentWeekKey(weeks) === sunday;
                        const canDrill = week != null && sunday;
                        const annotation = getAnnotationForCell(metric.metric_id, sunday);
                        return (
                          <TableCell
                            key={sunday}
                            align="center"
                            sx={{
                              cursor: (isManualEntry || canDrill) ? 'pointer' : 'default',
                              backgroundColor: isCurrentWeek ? 'rgba(var(--accent-primary-rgb), 0.04)' : undefined,
                              '&:hover': (isManualEntry || canDrill) ? { backgroundColor: 'rgba(var(--accent-primary-rgb), 0.1)' } : undefined,
                              transition: 'background-color 0.15s',
                              position: 'relative',
                            }}
                            onClick={() => {
                              if (isManualEntry) handleAddValue(metric, sunday);
                              else if (canDrill) openDrillDown(metric.metric_id, metric.metric_name, sunday, metric.unit);
                            }}
                            onContextMenu={(e) => {
                              e.preventDefault();
                              setAnnotationNoteInput(annotation?.note || '');
                              setAnnotationPopover({ anchorEl: e.currentTarget, metricId: metric.metric_id, metricName: metric.metric_name, weekStart: sunday });
                            }}
                            onDoubleClick={(e) => {
                              if (e.ctrlKey || e.metaKey) return;
                              e.stopPropagation();
                              setAnnotationNoteInput(annotation?.note || '');
                              setAnnotationPopover({ anchorEl: e.currentTarget as unknown as HTMLElement, metricId: metric.metric_id, metricName: metric.metric_name, weekStart: sunday });
                            }}
                          >
                            <Tooltip
                              title={
                                <Box>
                                  {annotation && <Typography variant="caption" display="block">Note: {annotation.note}</Typography>}
                                  <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.7 }}>{isManualEntry ? 'Click to edit' : canDrill ? 'Click for details' : ''} • Right-click to annotate</Typography>
                                </Box>
                              }
                              placement="top"
                            >
                              <Box sx={{ position: 'relative' }}>
                                {annotation && (
                                  <Box sx={{ position: 'absolute', top: 2, right: 2, width: 5, height: 5, borderRadius: '50%', bgcolor: 'primary.main' }} />
                                )}
                                <Typography component="div" variant="body2" fontWeight={500} sx={{ fontSize: '0.78rem', color: (week?.weekly_actual != null && week.weekly_actual < 0) ? '#f44336' : undefined }}>
                                  {formatValue(week?.weekly_actual, metric.unit)}
                                </Typography>
                              </Box>
                            </Tooltip>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                }

                // -- KPI metric: Target row + Actual row --
                return (
                  <React.Fragment key={metric.metric_id}>
                    {/* Target Row */}
                    <TableRow sx={{ '& td': { py: 0.3, borderBottom: 'none' }, backgroundColor: rowBg, '&:hover': { backgroundColor: 'rgba(var(--accent-primary-rgb), 0.06)' } }}>
                      <TableCell
                        rowSpan={2}
                        sx={{
                          position: 'sticky', left: 0, backgroundColor: metricIndex % 2 === 0 ? stickyBgEven : stickyBgOdd, zIndex: 9,
                          borderRight: '2px solid', borderColor: 'divider', borderBottom: '1px solid rgba(var(--overlay-rgb),0.06)',
                          verticalAlign: 'middle',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minHeight: 36 }}>
                          <Tooltip
                            title={
                              <Box sx={{ maxWidth: 260 }}>
                                <Typography variant="caption" fontWeight={600} display="block">{metric.metric_name}</Typography>
                                {metric.description && <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.85 }}>{metric.description}</Typography>}
                                <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.7 }}>{getMetricSourceLabel(metric)}</Typography>
                                {metric.department_name && <Chip label={metric.department_name} size="small" sx={{ mt: 0.5, height: 18, fontSize: '10.5px' }} />}
                              </Box>
                            }
                            placement="right"
                          >
                            <Typography variant="body2" fontWeight={500} noWrap sx={{ flex: 1, cursor: 'default', fontSize: '12.5px' }}>
                              {metric.metric_name}
                            </Typography>
                          </Tooltip>
                          <Box
                            sx={{ cursor: 'pointer', '&:hover': { opacity: 1, transform: 'scaleY(1.3)' }, transition: 'all 0.15s' }}
                            onClick={(e) => { e.stopPropagation(); openSparklineDetail(metric.metric_id, metric.metric_name, metric.unit); }}
                            title="Click to view detailed trend"
                          >
                            <Sparkline data={metric.weeks.map(w => w.weekly_actual)} color="auto" />
                          </Box>
                        </Box>
                      </TableCell>
                      {/* Summary  -  Target */}
                      <TableCell align="center" sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 0.5 }}>
                          <Typography component="span" variant="caption" sx={{ fontSize: '10.5px', opacity: 0.5, textTransform: 'uppercase' }}>Trg</Typography>
                          <Typography component="span" variant="body2" fontWeight={500} sx={{ fontSize: '0.78rem' }}>
                            {formatValue(metric.quarter_target, metric.unit)}
                          </Typography>
                        </Box>
                      </TableCell>
                      {/* Pace  -  spans both rows */}
                      <TableCell
                        rowSpan={2}
                        align="center"
                        sx={{ borderRight: '1px solid', borderColor: 'divider', borderBottom: '1px solid rgba(var(--overlay-rgb),0.06)' }}
                      >
                        {(() => {
                          const weekKey = getCurrentWeekKey(weeks);
                          const week = metric.weeks.find((w) => w.week_start === weekKey);
                          if (!isCurrentQuarterView() || !week || metric.quarter_target === null) {
                            return <Typography component="div" variant="caption" sx={{ opacity: 0.4 }}> - </Typography>;
                          }

                          let delta: number;
                          let color = 'warning.main';
                          if (metric.target_type === 'threshold') {
                            delta = week.average_actual - week.weekly_target;
                            if (metric.threshold_direction === 'below') {
                              if (delta < 0) color = '#4caf50'; if (delta > 0) color = '#f44336';
                            } else {
                              if (delta > 0) color = '#4caf50'; if (delta < 0) color = '#f44336';
                            }
                          } else {
                            delta = metric.evaluation_mode === 'rolling'
                              ? week.average_actual - week.average_target
                              : week.cumulative_actual - week.cumulative_target;
                            if (delta > 0) color = '#4caf50'; if (delta < 0) color = '#f44336';
                          }

                          const formatted = formatValue(Math.abs(delta), null);
                          const display = delta > 0 ? `+${formatted}` : (delta < 0 ? `-${formatted}` : '0');
                          return (
                            <Chip
                              label={display}
                              size="small"
                              sx={{
                                backgroundColor: `${color}22`,
                                color: color,
                                fontWeight: 700,
                                fontSize: '11px',
                                height: 22,
                                border: `1px solid ${color}44`,
                              }}
                            />
                          );
                        })()}
                      </TableCell>
                      {/* Weekly targets */}
                      {metricWeeks.map((week, index) => {
                        const sunday = weeks[index];
                        const isCurrentWeek = getCurrentWeekKey(weeks) === sunday;
                        return (
                          <TableCell
                            key={sunday}
                            align="center"
                            sx={{
                              cursor: 'pointer',
                              backgroundColor: isCurrentWeek ? 'rgba(var(--accent-primary-rgb), 0.04)' : undefined,
                              '&:hover': { backgroundColor: 'rgba(var(--accent-primary-rgb), 0.1)' },
                              transition: 'background-color 0.15s',
                            }}
                            onClick={() => handleAddValue(metric, weeks[index])}
                          >
                            <Typography component="div" variant="caption" sx={{ fontSize: '11px', opacity: 0.5 }}>
                              {formatValue(week?.weekly_target, metric.unit)}
                            </Typography>
                          </TableCell>
                        );
                      })}
                    </TableRow>

                    {/* Actuals Row */}
                    <TableRow sx={{ '& td': { py: 0.3, borderBottom: '1px solid rgba(var(--overlay-rgb),0.06)' }, backgroundColor: rowBg, '&:hover': { backgroundColor: 'rgba(var(--accent-primary-rgb), 0.06)' } }}>
                      {/* Summary  -  Actual */}
                      <TableCell align="center" sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 0.5 }}>
                          <Typography component="span" variant="caption" sx={{ fontSize: '10.5px', opacity: 0.5, textTransform: 'uppercase' }}>
                            {metric.target_type === 'threshold' ? 'Avg' : 'Act'}
                          </Typography>
                          <Typography component="span" variant="body2" fontWeight={700} sx={{ fontSize: '0.78rem', color: (metric.quarter_actual != null && metric.quarter_actual < 0) ? '#f44336' : undefined }}>
                            {formatValue(metric.quarter_actual, metric.unit)}
                          </Typography>
                        </Box>
                      </TableCell>
                      {/* Weekly actuals */}
                      {metricWeeks.map((week, index) => {
                        const sunday = weeks[index];
                        const isThreshold = metric.target_type === 'threshold';
                        const isCurrentWeek = getCurrentWeekKey(weeks) === sunday;
                        const isOnTrack = week
                          ? isThreshold
                            ? metric.threshold_direction === 'below'
                              ? week.weekly_actual <= week.weekly_target
                              : week.weekly_actual >= week.weekly_target
                            : week.weekly_actual >= week.weekly_target
                          : false;
                        const hasValue = week && week.weekly_actual != null;

                        // Color the number text: green if on track, red if off track
                        const valueColor = hasValue
                          ? isOnTrack ? 'success.main' : 'error.main'
                          : undefined;
                        const canDrill = week != null && sunday;

                        const annotation = getAnnotationForCell(metric.metric_id, sunday);
                        return (
                          <TableCell
                            key={sunday}
                            align="center"
                            sx={{
                              cursor: (isManualEntry || canDrill) ? 'pointer' : 'default',
                              backgroundColor: isCurrentWeek ? 'rgba(var(--accent-primary-rgb), 0.04)' : undefined,
                              '&:hover': (isManualEntry || canDrill) ? { backgroundColor: 'rgba(var(--accent-primary-rgb), 0.1)' } : undefined,
                              transition: 'background-color 0.15s',
                              position: 'relative',
                            }}
                            onClick={() => {
                              if (isManualEntry) handleAddValue(metric, sunday);
                              else if (canDrill) openDrillDown(metric.metric_id, metric.metric_name, sunday, metric.unit);
                            }}
                            onContextMenu={(e) => {
                              e.preventDefault();
                              setAnnotationNoteInput(annotation?.note || '');
                              setAnnotationPopover({ anchorEl: e.currentTarget, metricId: metric.metric_id, metricName: metric.metric_name, weekStart: sunday });
                            }}
                            onDoubleClick={(e) => {
                              if (e.ctrlKey || e.metaKey) return;
                              e.stopPropagation();
                              setAnnotationNoteInput(annotation?.note || '');
                              setAnnotationPopover({ anchorEl: e.currentTarget as unknown as HTMLElement, metricId: metric.metric_id, metricName: metric.metric_name, weekStart: sunday });
                            }}
                          >
                            <Tooltip
                              title={
                                <Box>
                                  {week ? (
                                    <>
                                      <Typography variant="caption" display="block">Actual: {formatValue(week.weekly_actual, metric.unit)}</Typography>
                                      <Typography variant="caption" display="block">Target: {formatValue(week.weekly_target, metric.unit)}</Typography>
                                      {hasValue && <Typography variant="caption" display="block" sx={{ mt: 0.5, color: isOnTrack ? 'success.light' : 'error.light' }}>{isOnTrack ? 'On track' : 'Off track'}</Typography>}
                                    </>
                                  ) : null}
                                  {annotation && <Typography variant="caption" display="block" sx={{ mt: 0.5, borderTop: '1px solid rgba(255,255,255,0.2)', pt: 0.5 }}>Note: {annotation.note}</Typography>}
                                  <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.7 }}>{isManualEntry ? 'Click to edit' : canDrill ? 'Click for details' : ''} • Right-click to annotate</Typography>
                                </Box>
                              }
                              placement="top"
                            >
                              <Box sx={{ position: 'relative' }}>
                                {annotation && (
                                  <Box sx={{ position: 'absolute', top: 2, right: 2, width: 5, height: 5, borderRadius: '50%', bgcolor: 'primary.main' }} />
                                )}
                                <Typography component="div" variant="body2" fontWeight={600} sx={{ fontSize: '0.78rem', color: valueColor }}>
                                  {formatValue(week?.weekly_actual, metric.unit)}
                                </Typography>
                              </Box>
                            </Tooltip>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
          </Box>
        </Box>
      )}
          </Box>
        </React.Fragment>

      {/* Metric edit/delete menu moved to Settings page */}

      <AddValueDialog
        open={addValueDialogOpen}
        onClose={() => {
          setAddValueDialogOpen(false);
          setSelectedMetric(null);
          setSelectedWeek(null);
          setSelectedWeekCurrentValue(undefined);
        }}
        onSuccess={() => {
          handleValueAdded();
        }}
        metric={selectedMetric}
        defaultDate={selectedWeek || undefined}
        defaultValue={selectedWeekCurrentValue}
      />
      <MetricVisibilityDialog
        open={visibilityDialogOpen}
        onClose={() => setVisibilityDialogOpen(false)}
        dashboardId={null}
        departmentId={
          showScorecardsChrome
            ? null
            : departments.find((dept) => dept.name === selectedDepartment)?.id || null
        }
        isMixed={showScorecardsChrome}
        onSaved={async () => {
          const hiddenIds = await loadVisibility();
          await loadScorecard(hiddenIds);
        }}
      />
      <CreateCustomDashboardDialog
        open={createDashboardDialogOpen}
        onClose={() => {
          setCreateDashboardDialogOpen(false);
          setCreateDashboardDialogDepartmentId(null);
        }}
        onSuccess={handleCreateDashboardSuccess}
        isMixed={showScorecardsChrome}
        departmentId={showScorecardsChrome ? null : createDashboardDialogDepartmentId}
      />
      <EditCustomDashboardDialog
        open={editDashboardDialogOpen}
        onClose={() => setEditDashboardDialogOpen(false)}
        dashboard={null}
        onSuccess={async () => {
          await loadDashboards();
          if (activeDashboardId) {
            const { metricIds } = await dashboardsApi.getMetrics(activeDashboardId);
            setCustomDashboardMetricIds(metricIds);
          }
        }}
      />
      <Snackbar
        open={undoSnackbarOpen}
        autoHideDuration={10000}
        onClose={handleCloseUndoSnackbar}
        message={`Metric "${undoMetricName}" removed.`}
        action={
          <Button color="primary" size="small" onClick={handleUndoDelete}>
            Undo
          </Button>
        }
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
      {/* -- Scorecard Save View Dialog -- */}
      <Dialog open={scorecardSaveDialogOpen} onClose={() => setScorecardSaveDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{scorecardSaveViewMode === 'update' ? 'Update Scorecard View' : 'Save Scorecard View'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, mt: 1 }}>
            <Button
              size="small"
              variant={scorecardSaveViewMode === 'new' ? 'contained' : 'outlined'}
              onClick={() => { setScorecardSaveViewMode('new'); setScorecardSaveViewTargetId(null); setScorecardSaveViewName(''); }}
            >
              Create new
            </Button>
            <Button
              size="small"
              variant={scorecardSaveViewMode === 'update' ? 'contained' : 'outlined'}
              onClick={() => {
                setScorecardSaveViewMode('update');
                if (scorecardSavedViews.length > 0) {
                  const first = scorecardSavedViews[0];
                  setScorecardSaveViewTargetId(first.id);
                  setScorecardSaveViewName(first.name);
                }
              }}
              disabled={scorecardSavedViews.length === 0}
            >
              Update existing
            </Button>
          </Box>

          {scorecardSaveViewMode === 'update' && scorecardSavedViews.length > 0 && (
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel id="scorecard-sv-target">View to update</InputLabel>
              <Select
                labelId="scorecard-sv-target"
                label="View to update"
                value={scorecardSaveViewTargetId ?? ''}
                onChange={(e) => {
                  const id = Number(e.target.value);
                  setScorecardSaveViewTargetId(id);
                  const view = scorecardSavedViews.find((v) => v.id === id);
                  if (view) setScorecardSaveViewName(view.name);
                }}
              >
                {scorecardSavedViews.map((v) => (
                  <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            autoFocus={scorecardSaveViewMode === 'new'}
            margin="dense"
            label="View name"
            fullWidth
            variant="outlined"
            value={scorecardSaveViewName}
            onChange={(e) => setScorecardSaveViewName(e.target.value)}
            placeholder="e.g. Mike's Weekly Review"
          />

          {/* Show current filters summary */}
          {scorecardCustomFilters.departments && scorecardCustomFilters.departments.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Departments: {scorecardCustomFilters.departments.join(', ')}
            </Typography>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {scorecardSaveViewMode === 'update'
              ? 'Current filters will replace the selected view.'
              : 'Current filters will be saved as a new view.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScorecardSaveDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => {
              const name = scorecardSaveViewName.trim();
              if (!name) return;
              const filtersToSave = { ...scorecardCustomFilters };

              if (scorecardSaveViewMode === 'update' && scorecardSaveViewTargetId != null) {
                settingsApi.updateScorecardView(scorecardSaveViewTargetId, name, filtersToSave).then((res) => {
                  if (res.success) {
                    setScorecardSaveDialogOpen(false);
                    setScorecardSaveViewName('');
                    setScorecardSavedViews((prev) =>
                      prev.map((v) => v.id === scorecardSaveViewTargetId ? { ...v, name, filters: filtersToSave } : v)
                    );
                    setScorecardActiveView(scorecardSaveViewTargetId);
                    setScorecardCustomFilters(filtersToSave);
                  }
                });
              } else {
                settingsApi.createScorecardView(name, filtersToSave).then((res) => {
                  if (res.success && res.data) {
                    setScorecardSaveDialogOpen(false);
                    setScorecardSaveViewName('');
                    const newView = { id: res.data!.id, name: res.data!.name, filters: res.data!.filters, position: 0, created_at: '' };
                    setScorecardSavedViews((prev) => [...prev, newView]);
                    setScorecardActiveView(res.data!.id);
                    setScorecardCustomFilters(filtersToSave);
                  }
                });
              }
            }}
            variant="contained"
            disabled={!scorecardSaveViewName.trim() || (scorecardSaveViewMode === 'update' && scorecardSaveViewTargetId == null)}
          >
            {scorecardSaveViewMode === 'update' ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      <MetricDrillDownDialog
        open={drillDownOpen}
        onClose={() => setDrillDownOpen(false)}
        metricId={drillDownMetricId}
        metricName={drillDownMetricName}
        week={drillDownWeek}
        unit={drillDownUnit}
      />
      {sparklineDetailOpen && sparklineDetailMetricId > 0 && (
        <SparklineDetailDialog
          open={sparklineDetailOpen}
          onClose={() => setSparklineDetailOpen(false)}
          metricId={sparklineDetailMetricId}
          metricName={sparklineDetailMetricName}
          unit={sparklineDetailUnit}
          quarterStart={quarterInfo?.start_date}
          quarterEnd={quarterInfo?.end_date}
        />
      )}

      {/* Metric Annotation Popover */}
      <Popover
        open={Boolean(annotationPopover)}
        anchorEl={annotationPopover?.anchorEl || null}
        onClose={() => { setAnnotationPopover(null); setAnnotationNoteInput(''); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box sx={{ p: 2, width: 280 }}>
          <Typography variant="subtitle2" gutterBottom>
            {annotationPopover?.metricName} — {annotationPopover?.weekStart ? new Date(annotationPopover.weekStart + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
          </Typography>
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            placeholder="Add a note (e.g. Holiday week)"
            value={annotationNoteInput}
            onChange={e => setAnnotationNoteInput(e.target.value)}
            sx={{ mb: 1 }}
          />
          <Box display="flex" gap={1} justifyContent="flex-end">
            {getAnnotationForCell(annotationPopover?.metricId ?? 0, annotationPopover?.weekStart ?? '') && (
              <Button size="small" color="error" onClick={handleDeleteAnnotation}>Remove</Button>
            )}
            <Button size="small" onClick={() => { setAnnotationPopover(null); setAnnotationNoteInput(''); }}>Cancel</Button>
            <Button size="small" variant="contained" disabled={annotationSaving || !annotationNoteInput.trim()} onClick={handleSaveAnnotation}>Save</Button>
          </Box>
        </Box>
      </Popover>
      </Box>
      <FeatureRequestDialog open={featureRequestOpen} onClose={() => setFeatureRequestOpen(false)} />
      <WhatsNewDialog open={whatsNewOpen} onClose={() => setWhatsNewOpen(false)} onUnreadChange={setWhatsNewUnread} />
    </Box>
  );
};

export default AppShell;
